import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../services/axiosInstance';
import { IAsset, ICategory, ICollection, IBadge, ITemplate } from '../types/damTypes';
import type { ValidooImageStatusState } from '../types/damTypes';
import { useCategoryContext } from '../hooks/useCategoryContext';
import { useStockLevels } from '../hooks/useStockLevels';
import { useDuplicateHandling } from '../hooks/useDuplicateHandling';
import { useCollectionActions } from '../hooks/useCollectionActions';
import { useDragSelection } from '../hooks/useDragSelection';
import { isStoreAsset } from '../utils/storeAssetHelpers';
import { decodeFileName } from '../utils/fileNameHelpers';
import { API_BASE_URL } from '../config';
import { observePreview } from '../services/previewService';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Select, FormControl, InputLabel, SelectChangeEvent, Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup, FormControlLabel, Radio, TextField, CircularProgress, DialogContentText, Alert, Badge, Chip, Slider, Snackbar, Tooltip } from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Inventory2Outlined';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import SortIcon from '@mui/icons-material/Sort';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CollectionsIcon from '@mui/icons-material/Collections';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import TerrainIcon from '@mui/icons-material/Terrain';
import LandscapeIcon from '@mui/icons-material/Landscape';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import PaletteIcon from '@mui/icons-material/Palette';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LensIcon from '@mui/icons-material/Lens';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BadgeFilter from './BadgeFilter';
import DuplicateMergeDialog from '../features/duplicates/DuplicateMergeDialog';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import { IPdfTemplate } from '../types/pdfTemplateTypes';
import { getActivePdfTemplates, downloadPdf, downloadMultiPdf } from '../services/pdfTemplateService';
import { filterTemplatesForSelection } from '../utils/pdfTemplateMenuUtils';
import {
  ActiveDownloadTemplate,
  BuiltInDownloadTemplateId,
  downloadAssetsWithBuiltInTemplate,
  fetchActiveDownloadTemplates as fetchActiveImageDownloadTemplates,
  getAvailableBuiltInDownloadTemplates,
  runCustomAssetDownloadTemplate,
} from '../utils/assetDownloadMenuUtils';
import {
  useValidooExport,
  ValidooExportBlockedDialog,
  ValidooExportSuccessDialog,
} from '../modules/validooExport';
import RelationImpactDialog from '../modules/referenceImpact/RelationImpactDialog';
import { isAssetUnderNamedCategoryRoot } from '../modules/referenceImpact/utils';
import {
  getWordPressArchiveUnpublishImpacts,
  requestWordPressArchiveUnpublish,
  WordPressArchiveUnpublishImpact,
} from '../modules/wordpressPublishing/archiveUnpublishGuard';

interface AssetGridProps {
  assets: IAsset[];
  totalAssetCount?: number;
  hasMoreAssets?: boolean;
  onLoadMoreAssets?: () => void;
  loading: boolean;
  error: Error | null;
  onAssetSelect: (assetId: string) => void;
  allCategories: ICategory[]; 
  collections: ICollection[]; 
  multiSelectEnabled?: boolean; 
  selectedAssetIds?: string[]; 
  onSelectedAssetsChange?: (selectedIds: string[]) => void; 
  onSelectionAnchorChange?: (assetId: string | null) => void;
  thumbnailSize?: number; 
  onThumbnailSizeChange?: (event: Event, newValue: number | number[]) => void;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  onCollectionUpdated?: () => void;
  onCartChange?: (cartItems: {[assetId: string]: number}) => void; // For notifying parent about cart changes
  onManageAssetBadges?: (asset: IAsset | null, assetIds?: string[]) => void;
  selectedBadgeIds?: string[];
  onBadgeToggle?: (badgeId: string) => void;
  onClearBadges?: () => void;
  availableBadges?: IBadge[]; // All configured badges from server (not limited to loaded assets)
  badgeFilterMode?: 'AND' | 'OR';
  onBadgeFilterModeToggle?: () => void;
  // New: currently active category filter selection from parent
  activeCategoryIds?: string[];
  // Templates for extracting chain fields
  templates?: ITemplate[];
  // Chain filter state from parent
  chainFilterKey?: string | null;
  onChainFilterChange?: (key: string | null) => void;
  wineFilterKey?: string | null;
  onWineFilterChange?: (key: string | null) => void;
  kontorFilter?: 'all' | 'true' | 'false';
  onKontorFilterChange?: (value: 'all' | 'true' | 'false') => void;
  popularAiTags?: { tag: string; count: number }[];
  onAiTagToggle?: (tag: string) => void;
  selectedAiTags?: string[];
  popularAiTagsLoading?: boolean;
  compactToolbar?: boolean; // Landscape mobile: minimal toolbar
  tabletToolbar?: boolean;
  activeCollectionId?: string | null;
  onRemoveFromCollection?: (assetIds: string[]) => void;
  onSortOrderChange?: (sortOrder: string) => void;
}

// Add a formatting helper function to better display file sizes
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
};

const getValidooImageStatusState = (asset: IAsset): ValidooImageStatusState => (
  asset.validooImageStatus?.state || 'unchecked'
);

const getCategoryRefId = (categoryRef: unknown): string => {
  if (!categoryRef) return '';
  if (typeof categoryRef === 'string') return categoryRef;
  if (typeof categoryRef === 'object' && '_id' in categoryRef) {
    const id = (categoryRef as { _id?: unknown })._id;
    return id ? String(id) : '';
  }
  return String(categoryRef);
};

const getValidooStatusPalette = (state: ValidooImageStatusState) => {
  if (state === 'approved') {
    return {
      backgroundColor: '#43a047',
      borderColor: '#2e7d32',
      label: 'Bild godkänd för Validoo',
    };
  }

  if (state === 'rejected') {
    return {
      backgroundColor: '#e53935',
      borderColor: '#b71c1c',
      label: 'Bild ej godkänd för Validoo',
    };
  }

  return {
    backgroundColor: '#9e9e9e',
    borderColor: '#757575',
    label: 'Bild ej kontrollerad för Validoo',
  };
};

const formatValidooFailedCheckLabel = (code: string, minRequiredSidePx?: number): string => {
  switch (code) {
    case 'IN_WINE_CATEGORY':
      return 'Kategori Vin';
    case 'SOURCE_EXISTS':
      return 'Originalfil finns';
    case 'INVALID_FORMAT':
      return 'JPG/JPEG-format';
    case 'COLORSPACE_RGB':
      return 'RGB-färgrymd';
    case 'DPI_NOT_300':
      return '300 DPI';
    case 'AT_LEAST_ONE_SIDE_MIN_PX':
      return `Minst en sida ${minRequiredSidePx || 2500} px`;
    case 'CLIPPING_PATH_1':
      return 'Urklippsbana "Path 1"';
    case 'INSPECTION_ERROR':
      return 'Bildanalys';
    case 'ASSET_NOT_FOUND':
      return 'Asset hittades';
    default:
      return code;
  }
};

const getValidooStatusTooltip = (
  asset: IAsset,
  label: string,
  checkedAt: string | null,
): React.ReactNode => {
  const status = asset.validooImageStatus;
  const failedCheckCodes = (status?.failedCheckCodes || []).filter(Boolean);

  if (status?.state !== 'rejected' || failedCheckCodes.length === 0) {
    return checkedAt
      ? `${label} (${checkedAt})`
      : label;
  }

  const uniqueFailedCheckCodes = failedCheckCodes.filter((code, index) => (
    failedCheckCodes.indexOf(code) === index
  ));

  return (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.25, opacity: 0.9 }}>
        Underkända kontroller:
      </Typography>
      <Box component="ul" sx={{ my: 0.5, pl: 2.25, pr: 0.5 }}>
        {uniqueFailedCheckCodes.map((code) => (
          <Box
            component="li"
            key={code}
            sx={{ typography: 'body2', lineHeight: 1.4 }}
          >
            {formatValidooFailedCheckLabel(code, status?.minRequiredSidePx)}
          </Box>
        ))}
      </Box>
      {checkedAt && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
          Kontrollerad {checkedAt}
        </Typography>
      )}
    </Box>
  );
};

const renderValidooStatusGlyph = (state: ValidooImageStatusState, size = 15) => {
  const palette = getValidooStatusPalette(state);
  const fontSize = Math.max(8, Math.round(size * 0.65));

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '3px',
        backgroundColor: palette.backgroundColor,
        border: `1px solid ${palette.borderColor}`,
        color: '#ffffff',
        fontWeight: 700,
        fontSize: `${fontSize}px`,
        lineHeight: 1,
        boxSizing: 'border-box',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      V
    </span>
  );
};

const ValidooStatusBadge: React.FC<{ asset: IAsset; size?: number }> = ({ asset, size = 15 }) => {
  const state = getValidooImageStatusState(asset);
  const palette = getValidooStatusPalette(state);
  const checkedAt = asset.validooImageStatus?.checkedAt
    ? new Date(asset.validooImageStatus.checkedAt).toLocaleString('sv-SE')
    : null;
  const tooltip = getValidooStatusTooltip(asset, palette.label, checkedAt);

  return (
    <Tooltip title={tooltip} arrow>
      <span style={{ display: 'inline-flex' }}>
        {renderValidooStatusGlyph(state, size)}
      </span>
    </Tooltip>
  );
};

const GRID_THUMB_IMG_STYLE: React.CSSProperties = {
  maxWidth: '100%', maxHeight: '100%', width: 'auto', height: '100%',
  objectFit: 'contain', backgroundColor: 'transparent', margin: 0,
};

const LIST_CONTAINER_STYLE: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '8px',
  padding: '8px', width: '100%', boxSizing: 'border-box',
};

const LIST_THUMB_CONTAINER_STYLE: React.CSSProperties = {
  width: '80px', height: '80px', flexShrink: 0, position: 'relative',
  borderRadius: '8px 0 0 8px', overflow: 'hidden', backgroundColor: '#f5f5f5',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const LIST_THUMB_IMG_STYLE: React.CSSProperties = {
  maxWidth: '74px', maxHeight: '74px', objectFit: 'contain',
  backgroundColor: 'transparent', margin: '3px',
};

const OVERLAY_STACK_STYLE: React.CSSProperties = {
  position: 'absolute', bottom: '8px', left: '8px', zIndex: 20,
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  gap: '3px', pointerEvents: 'auto',
};

const VIDEO_PREVIEW_STYLE: React.CSSProperties = {
  width: '100%', height: '100%', objectFit: 'contain',
  backgroundColor: 'transparent', margin: 0, borderRadius: '4px',
};

const DUPLICATE_TRIANGLE_GRID_STYLE: React.CSSProperties = {
  position: 'absolute', top: 0, left: 0, width: 0, height: 0,
  borderStyle: 'solid', borderWidth: '28px 28px 0 0',
  borderColor: '#ffa726 transparent transparent transparent',
  cursor: 'pointer', zIndex: 10,
  filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))',
};

const DUPLICATE_TRIANGLE_LIST_STYLE: React.CSSProperties = {
  position: 'absolute', top: 0, left: 0, width: 0, height: 0,
  borderStyle: 'solid', borderWidth: '20px 20px 0 0',
  borderColor: '#ffa726 transparent transparent transparent',
  cursor: 'pointer', zIndex: 10,
};

interface GridAssetCardProps {
  asset: IAsset;
  index: number;
  isSelected: boolean;
  isDuplicate: boolean;
  isStoreAsset: boolean;
  isKontorAsset: boolean;
  stockLevel: number | undefined;
  stockLevelsLoading: boolean;
  isUnderProducts: boolean;
  isUnderWine: boolean;
  isProductsAsset: boolean;
  isWineAsset: boolean;
  isRecipeAsset: boolean;
  preset: { thumb: number; fontTitle: number; fontMeta: number; badgeScale: number };
  imageInnerPadding: number;
  textPadTop: number;
  textPadX: number;
  textPadBottom: number;
  cardTitle: string;
  productMetaLine: string;
  wineMetaLine: string;
  categoryById: Map<string, ICategory>;
  onAssetClick: (asset: IAsset, index: number, e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent, asset: IAsset) => void;
  onMenuOpen: (e: React.MouseEvent<HTMLButtonElement>, asset: IAsset) => void;
  onDuplicateClick: (e: React.MouseEvent, assetId: string) => void;
  onAddToCart: (assetId: string) => void;
  getStockColor: (level: number) => string;
  isBadgeVisibleInCurrentView: (badge: any) => boolean;
}

const GridAssetCard = React.memo<GridAssetCardProps>(({
  asset, index, isSelected, isDuplicate, isStoreAsset, isKontorAsset,
  stockLevel, stockLevelsLoading,
  isUnderProducts, isUnderWine, isProductsAsset, isWineAsset, isRecipeAsset,
  preset, imageInnerPadding, textPadTop, textPadX, textPadBottom,
  cardTitle, productMetaLine, wineMetaLine,
  categoryById,
  onAssetClick, onDragStart, onMenuOpen, onDuplicateClick,
  onAddToCart, getStockColor, isBadgeVisibleInCurrentView,
}) => {
  const titleFontSize = Math.max(10, preset.fontTitle - 1);
  const recipeTitleMinHeight = Math.ceil(titleFontSize * 1.15 * 2);

  return (
    <div
      data-asset-card
      data-asset-id={asset._id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0',
        border: isSelected ? '2px solid #072f53' : '1px solid #d4d4d4',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: 'white',
        boxShadow: isSelected ? '0 0 8px rgba(7, 47, 83, 0.5)' : 'none',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        transform: isSelected ? 'translateY(-3px)' : 'translateY(0)',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={(e) => onAssetClick(asset, index, e)}
      draggable
      onDragStart={(e) => onDragStart(e, asset)}
      onMouseOver={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
      onMouseOut={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <div style={OVERLAY_STACK_STYLE}>
        {(isUnderProducts || isUnderWine) && (
          <ValidooStatusBadge asset={asset} />
        )}
        {isUnderProducts && !asset.skipDabasSync && asset.dabasSyncStatus === 'failed' && !asset.hasPreviousDabasSync && (
          <Tooltip title={`Dabas-synk misslyckades${asset.lastDabasSyncError ? `: ${asset.lastDabasSyncError}` : ''}`} arrow>
            <span style={{ display: 'inline-flex' }}><CancelIcon sx={{ color: '#f44336', fontSize: '15px' }} /></span>
          </Tooltip>
        )}
        {isUnderProducts && !asset.skipDabasSync && asset.dabasSyncStatus === 'failed' && asset.hasPreviousDabasSync && (
          <Tooltip title={`Tidigare synkad med Dabas men senaste synk misslyckades${asset.lastDabasSyncError ? `: ${asset.lastDabasSyncError}` : ''}`} arrow>
            <span style={{ display: 'inline-flex' }}><CancelIcon sx={{ color: '#ff9800', fontSize: '15px' }} /></span>
          </Tooltip>
        )}
        {isUnderProducts && !asset.skipDabasSync && asset.dabasSyncStatus === 'success' && (
          <Tooltip title={`Synkad med Dabas${asset.lastDabasSyncAt ? ` (${new Date(asset.lastDabasSyncAt).toLocaleString('sv-SE')})` : ''}`} arrow>
            <span style={{ display: 'inline-flex' }}><CheckCircleIcon sx={{ color: '#4caf50', fontSize: '15px' }} /></span>
          </Tooltip>
        )}
        {isUnderProducts && !!asset.skipDabasSync && (
          <Tooltip title="Synkar inte med Dabas" arrow>
            <span style={{ display: 'inline-flex' }}><SyncDisabledIcon sx={{ color: '#8a8a8a', fontSize: '15px' }} /></span>
          </Tooltip>
        )}
        {isKontorAsset && (
          <Tooltip title="Kontoret" arrow>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#cdcdcd', color: '#fff', fontSize: '7px', fontWeight: 700, lineHeight: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>K</span>
          </Tooltip>
        )}
      </div>

      <div style={{
        flexGrow: 0, marginBottom: '0', position: 'relative',
        borderRadius: '8px 8px 0 0', overflow: 'hidden', width: '100%',
        height: `${preset.thumb}px`, backgroundColor: '#e9ecef',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: `${imageInnerPadding}px`,
      }}>
        {isDuplicate && (
          <Tooltip title="Denna bild har dubbletter - klicka för att hantera">
            <div onClick={(e) => onDuplicateClick(e, asset._id)} style={DUPLICATE_TRIANGLE_GRID_STYLE}>
              <span style={{ position: 'absolute', top: '-26px', left: '3px', color: 'white', fontSize: '12px', fontWeight: 'bold', textShadow: '0 0 2px rgba(0,0,0,0.5)' }}>!</span>
            </div>
          </Tooltip>
        )}
        {asset.previewPaths?.thumbnail ? (
          (() => {
            const baseThumb = `${API_BASE_URL}${asset.previewPaths.thumbnail}`;
            const versionSource = asset.modifiedAt || asset.uploadedAt;
            const version = versionSource ? `?v=${new Date(versionSource).getTime()}` : '';
            const pngUrl = encodeURI(baseThumb) + version;
            const isVector = (asset.fileType || '').includes('postscript')
              || (asset.fileType || '').includes('illustrator')
              || (asset.fileType || '').includes('x-eps')
              || (asset.fileType || '').includes('pdf')
              || (asset.fileName || '').toLowerCase().endsWith('.ai')
              || (asset.fileName || '').toLowerCase().endsWith('.eps');
            const isVideo = (asset.fileType || '').startsWith('video/');
            if (isVector || isVideo) {
              const desiredSize = preset.thumb;
              const token = (typeof window !== 'undefined') ? localStorage.getItem('authToken') : null;
              let apiUrl = `${API_BASE_URL}/api/assets/${asset._id}/preview/thumbnail${token ? `?token=${encodeURIComponent(token)}` : ''}${token ? `&size=${desiredSize}` : `?size=${desiredSize}`}&format=webp`;
              requestAnimationFrame(() => {
                const el = document.getElementById(`thumb-${asset._id}`) as HTMLImageElement | null;
                if (el) observePreview(el, asset._id, 'thumbnail', desiredSize);
              });
              const initialSrc = apiUrl;
              if (isVideo && asset.previewPaths?.videoPreview) {
                const clipUrl = `${API_BASE_URL}${asset.previewPaths.videoPreview}${version}`;
                return (
                  <video src={clipUrl} poster={pngUrl} muted playsInline preload="none" style={VIDEO_PREVIEW_STYLE}
                    onMouseEnter={(e) => { try { (e.currentTarget as HTMLVideoElement).play().catch(() => {}); } catch {} }}
                    onMouseLeave={(e) => { try { const v = e.currentTarget as HTMLVideoElement; v.pause(); v.currentTime = 0; } catch {} }}
                  />
                );
              }
              return (
                <img id={`thumb-${asset._id}`} src={initialSrc} alt={asset.fileName} loading="lazy" decoding="async" style={GRID_THUMB_IMG_STYLE}
                  onError={(e) => {
                    const imgEl = e.currentTarget as HTMLImageElement;
                    if (asset.previewPaths?.thumbnail && !imgEl.dataset.altTried) {
                      imgEl.dataset.altTried = '1';
                      imgEl.src = `${API_BASE_URL}${asset.previewPaths.thumbnail}${version}`.replace(/%20/g, ' ');
                      return;
                    }
                    if (!imgEl.dataset.apiTried) { imgEl.dataset.apiTried = '1'; imgEl.src = apiUrl; return; }
                    imgEl.onerror = null; imgEl.src = '';
                  }}
                />
              );
            }
            const desiredSize = preset.thumb;
            try {
              const base = asset.previewPaths?.thumbnail || '';
              const lastSlash = base.lastIndexOf('/') + 1;
              const dir = base.substring(0, lastSlash);
              const file = base.substring(lastSlash);
              const dot = file.lastIndexOf('.');
              const name = dot > 0 ? file.substring(0, dot) : file;
              const sizedNameBase = name.replace(/-(thumbnail)(?:-\d+)?$/i, `-$1-${desiredSize}`);
              const sizedUrl = `${API_BASE_URL}${dir}${sizedNameBase}.webp`;
              const fallbackUrl = `${API_BASE_URL}${base}`;
              return (
                <img id={`thumb-${asset._id}`} src={sizedUrl} alt={asset.fileName} loading="lazy" decoding="async" style={GRID_THUMB_IMG_STYLE}
                  onError={(e) => { const el = e.currentTarget; if (!el.dataset.fallback) { el.dataset.fallback = '1'; el.src = fallbackUrl; } }}
                />
              );
            } catch {
              return <img id={`thumb-${asset._id}`} src={`${API_BASE_URL}${asset.previewPaths?.thumbnail || ''}`} alt={asset.fileName} loading="lazy" decoding="async" style={GRID_THUMB_IMG_STYLE} />;
            }
          })()
        ) : asset.fileType.startsWith('image/') ? (
          <img src={`${API_BASE_URL}/${asset.filePath.replace(/\\/g, '/')}`} alt={asset.fileName} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain', margin: 0 }} />
        ) : asset.fileType.startsWith('video/') && asset.previewPaths?.thumbnail ? (
          <img src={`${API_BASE_URL}${asset.previewPaths.thumbnail}`} alt={asset.fileName} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain', margin: 0 }} />
        ) : (
          <div style={{ background: 'transparent', padding: `${imageInnerPadding}px`, textAlign: 'center', height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', borderRadius: '4px', margin: 0 }}>
            <div style={{ fontSize: '40px', marginBottom: '10px', opacity: 0.7 }}>
              {asset.fileType.includes('pdf') ? '📄' : asset.fileType.includes('video') ? '🎬' : asset.fileType.includes('audio') ? '🎵' : asset.fileType.includes('postscript') || asset.fileName.toLowerCase().endsWith('.ai') ? '🎨' : '📁'}
            </div>
            <div>
              {isProductsAsset ? productMetaLine
                : isWineAsset ? wineMetaLine
                : asset.width && asset.height ? <>{asset.width} × {asset.height} px{asset.fileSize > 0 && <>{' | '}{formatFileSize(asset.fileSize)}</>}</>
                : <>{asset.fileType.split('/')[1] || asset.fileType}{asset.fileSize > 0 && <>{' · '}{formatFileSize(asset.fileSize)}</>}</>
              }
            </div>
          </div>
        )}

        <IconButton size="small" onClick={(e) => onMenuOpen(e as any, asset)}
          sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255, 255, 255, 0.8)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }, zIndex: 2 }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>

        {asset.isFavorite && (
          <span style={{ position: 'absolute', top: '8px', left: '8px', color: 'gold', fontSize: '1.5em', textShadow: '0 0 3px rgba(0,0,0,0.5)' }}>★</span>
        )}
        {isSelected && (
          <span style={{ position: 'absolute', top: '8px', left: '8px', color: '#072f53', fontSize: '1.5em', textShadow: '0 0 3px rgba(255,255,255,0.7)' }}>✓</span>
        )}

        {isStoreAsset && (
          <>
            <div style={{
              position: 'absolute', top: '8px',
              left: isSelected || asset.isFavorite ? '40px' : '8px',
              width: '24px', height: '24px', borderRadius: '50%',
              backgroundColor: stockLevel !== undefined ? getStockColor(stockLevel) : '#ccc',
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8em', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.3)', zIndex: 3,
            }}>
              {stockLevelsLoading ? '...' : (stockLevel || 0)}
            </div>
            <Button size="small" variant="contained"
              onClick={(e) => { e.stopPropagation(); onAddToCart(asset._id); }}
              disabled={stockLevel === 0}
              startIcon={<AddShoppingCartIcon />}
              sx={{
                position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                backgroundColor: '#072f53', color: 'white', minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem',
                '&:hover': { backgroundColor: '#051a2e' }, '&:disabled': { backgroundColor: '#ccc', color: '#666' }, zIndex: 3,
              }}
            >Beställ</Button>
          </>
        )}

        {asset.activeBadges && asset.activeBadges.length > 0 && (
          <div style={{ position: 'absolute', bottom: '8px', right: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '80%' }}>
            {asset.activeBadges.filter((b: any) => isBadgeVisibleInCurrentView(b)).slice(0, 3).map((b: any) => {
              const bName = b?.name || 'Badge';
              const bg = b?.color || '#072f53';
              const fg = b?.textColor || '#ffffff';
              const label = `${(b?.iconUrl ? '' : (b?.displayEmoji || ''))} ${(b?.showName !== false) ? bName : ''}`.trim() || bName;
              return (
                <span key={b?._id || b} style={{ backgroundColor: bg, color: fg, borderRadius: '12px', padding: '2px 6px', fontSize: '0.7rem', boxShadow: '0 1px 2px rgba(0,0,0,0.25)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {b?.iconUrl && <img src={b.iconUrl} alt="icon" style={{ width: '14px', height: '14px', borderRadius: '2px' }} />}
                  {label}
                </span>
              );
            })}
          </div>
        )}

        {asset.aiStatus && asset.aiStatus !== 'done' && (
          <div style={{ position: 'absolute', bottom: '8px', left: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '0.7rem' }}>
            AI: {asset.aiStatus}
          </div>
        )}
      </div>

      <div style={{ padding: `${textPadTop}px ${textPadX}px ${textPadBottom}px ${textPadX}px`, backgroundColor: 'white', borderRadius: '0 0 8px 8px' }}>
        <Tooltip title={cardTitle} enterDelay={1000} arrow>
          <p style={{
            margin: '0 0 4px 0',
            fontWeight: 'bold',
            fontSize: `${titleFontSize}px`,
            lineHeight: 1.15,
            letterSpacing: '-0.1px',
            textAlign: 'center',
            overflow: 'hidden',
            minHeight: isRecipeAsset ? `${recipeTitleMinHeight}px` : undefined,
            display: isRecipeAsset ? '-webkit-box' : 'block',
            WebkitLineClamp: isRecipeAsset ? 2 : undefined,
            WebkitBoxOrient: isRecipeAsset ? 'vertical' : undefined,
            whiteSpace: isRecipeAsset ? 'normal' : 'nowrap',
            textOverflow: isRecipeAsset ? undefined : 'ellipsis',
            wordBreak: isRecipeAsset ? 'break-word' : 'normal',
          }}>
            {cardTitle}
          </p>
        </Tooltip>
        {((isProductsAsset && !!productMetaLine) || (isWineAsset && !!wineMetaLine) || ((!isProductsAsset && !isWineAsset) && asset.width && asset.height) || (asset.categories && asset.categories.length > 0)) ? (
          <div style={{ fontSize: `${preset.fontMeta}px`, color: '#666', textAlign: 'center', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {isProductsAsset ? (productMetaLine ? <span style={{ color: '#888' }}>{productMetaLine}</span> : null)
              : isWineAsset ? (wineMetaLine ? <span style={{ color: '#888' }}>{wineMetaLine}</span> : null)
              : asset.width && asset.height ? (
                <span style={{ color: '#888' }}>{asset.width} × {asset.height} px{asset.fileSize > 0 && <>{' | '}{formatFileSize(asset.fileSize)}</>}</span>
              ) : null}
            {asset.categories && asset.categories.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4px' }}>
                {asset.categories.map(categoryRef => {
                  const categoryId = getCategoryRefId(categoryRef);
                  const category = categoryById.get(categoryId);
                  return category ? (
                    <span key={categoryId} style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' }}>{category.name}</span>
                  ) : null;
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
});

interface PermissionProps {
  canUpdate?: boolean;
  canManageBadges?: boolean;
  canAddToCollection?: boolean;
  canShare?: boolean;
  canArchive?: boolean;
  canSaveForValidoo?: boolean;
  canSortByWordPressPublishedAt?: boolean;
  canFilterKontorProducts?: boolean;
  canFilterKontorWine?: boolean;
}

function isCategoryIdUnderRoot(
  categoryId: string,
  rootId: string | null,
  categoryParentById: Map<string, string | null>,
): boolean {
  if (!rootId || !categoryId) return false;

  let current: string | null | undefined = categoryId;
  while (current) {
    if (current === rootId) return true;
    current = categoryParentById.get(current) || null;
  }

  return false;
}

const AssetGrid: React.FC<AssetGridProps & PermissionProps> = ({
  assets,
  totalAssetCount,
  hasMoreAssets = false,
  onLoadMoreAssets = () => {},
  loading,
  error,
  onAssetSelect,
  allCategories, 
  collections, 
  multiSelectEnabled = false,
  selectedAssetIds = [],
  onSelectedAssetsChange = () => {},
  onSelectionAnchorChange = () => {},
  thumbnailSize = 190, 
  onThumbnailSizeChange = () => {},
  viewMode = 'grid',
  onViewModeChange = () => {},
  onCollectionUpdated = () => {},
  onCartChange = () => {},
  onManageAssetBadges = () => {},
  selectedBadgeIds = [],
  onBadgeToggle = () => {},
  onClearBadges,
  availableBadges,
  badgeFilterMode = 'AND',
  onBadgeFilterModeToggle = () => {},
  activeCategoryIds = [],
  templates = [],
  chainFilterKey,
  onChainFilterChange,
  wineFilterKey,
  onWineFilterChange,
  kontorFilter = 'all',
  onKontorFilterChange = () => {},
  popularAiTags = [],
  onAiTagToggle = () => {},
  selectedAiTags = [],
  popularAiTagsLoading = false,
  compactToolbar = false,
  tabletToolbar = false,
  activeCollectionId = null,
  onRemoveFromCollection,
  onSortOrderChange = () => {},
  canUpdate = false,
  canManageBadges = false,
  canAddToCollection = false,
  canShare = false,
  canArchive = false,
  canSaveForValidoo = false,
  canSortByWordPressPublishedAt = false,
  canFilterKontorProducts = false,
  canFilterKontorWine = false,
}) => {
  // Local overrides so Kontor toggle reflects immediately without full refetch
  const [kontorOverrides, setKontorOverrides] = useState<Record<string, boolean>>({});
  const isKontor = (asset: IAsset): boolean => {
    const v = kontorOverrides[asset._id];
    return typeof v === 'boolean' ? v : !!asset.kontor;
  };
  const normalizedSelectedAiTags = React.useMemo(() => {
    return new Set((selectedAiTags || []).map(tag => tag.toLowerCase()));
  }, [selectedAiTags]);
  const isTabletToolbar = tabletToolbar && !compactToolbar;

  const selectedIdSet = React.useMemo(() => new Set(selectedAssetIds), [selectedAssetIds]);

  const {
    categoryById, categoryParentById,
    storeTypeCategoryId, setStoreTypeCategoryId,
    storeImagesRootId, isUnderStoreImages, selectedStoreChains, storeTypeOptions,
    productsRootId, isUnderProducts, wineRootId, isUnderWine, recipesRootId, isUnderRecipes,
    isBadgeVisibleInCurrentView,
  } = useCategoryContext(allCategories, activeCategoryIds);
  const showWordPressPublishedSort = canSortByWordPressPublishedAt && isUnderRecipes;

  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAssetForMenu, setSelectedAssetForMenu] = useState<IAsset | null>(null);
  const selectedMenuAssetId = selectedAssetForMenu?._id || null;
  const [sortOrder, setSortOrder] = useState<string>('newest');
  const userSelectedSortRef = React.useRef(false);
  const [dabasStatusFilter, setDabasStatusFilter] = useState<'all' | 'failed' | 'failed_previous' | 'success' | 'skip' | 'validoo_approved' | 'validoo_rejected' | 'validoo_unchecked'>('all');
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);
  
  // PDF templates state
  const [pdfTemplates, setPdfTemplates] = useState<IPdfTemplate[]>([]);
  const [loadingPdfTemplates, setLoadingPdfTemplates] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Lägg till en state för att fördröja visningen av "inga assets hittades"
  const [showNoAssetsMessage, setShowNoAssetsMessage] = useState(false);
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [relationImpactDialogOpen, setRelationImpactDialogOpen] = useState(false);
  const [relationImpactAssets, setRelationImpactAssets] = useState<IAsset[]>([]);
  const [relationImpactAssetIds, setRelationImpactAssetIds] = useState<string[]>([]);
  const [wordPressArchiveImpacts, setWordPressArchiveImpacts] = useState<WordPressArchiveUnpublishImpact[]>([]);
  const showSnackbar = (msg: string) => { setSnackbarMessage(msg); setSnackbarOpen(true); };

  // Infinite scroll sentinel for server-side paging
  const lazyObserverRef = React.useRef<IntersectionObserver | null>(null);
  const sentinelCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    if (lazyObserverRef.current) {
      lazyObserverRef.current.disconnect();
      lazyObserverRef.current = null;
    }
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMoreAssets && !loading) {
          onLoadMoreAssets();
        }
      },
      { rootMargin: '600px 0px' },
    );
    obs.observe(node);
    lazyObserverRef.current = obs;
  }, [hasMoreAssets, loading, onLoadMoreAssets]);

  const {
    addToCollectionDialogOpen, setAddToCollectionDialogOpen,
    selectedCollection, setSelectedCollection,
    newCollectionName, setNewCollectionName,
    createNewCollection, setCreateNewCollection,
    addingToCollection, collectionError,
    pendingCollectionAssetIds, setPendingCollectionAssetIds,
    resetDialog: resetCollectionDialog,
    handleAddToCollection: handleAddToCollectionCore,
  } = useCollectionActions(onCollectionUpdated, showSnackbar);

  // Shopping cart & stock levels (extracted to hook)
  const storeAssetIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const asset of assets) {
      if (isStoreAsset(asset, allCategories)) ids.add(asset._id);
    }
    return ids;
  }, [assets, allCategories]);

  const {
    cartItems, stockLevels, stockLevelsLoading,
    addToCart, getStockColor,
  } = useStockLevels(storeAssetIds.size > 0, onCartChange);

  const {
    duplicateAssetIds, setDuplicateAssetIds,
    duplicateModalOpen, setDuplicateModalOpen,
    duplicateModalAssetId, setDuplicateModalAssetId,
    duplicateClusterData, setDuplicateClusterData,
    duplicateClusterLoading,
    duplicateModalSelectedA, setDuplicateModalSelectedA,
    duplicateModalSelectedB, setDuplicateModalSelectedB,
    duplicateMerging, setDuplicateMerging,
    duplicateMergeDialogOpen, setDuplicateMergeDialogOpen,
    handleDuplicateIndicatorClick,
  } = useDuplicateHandling();

  const getAssetIdsForCurrentSelection = useCallback((assetIdOverride?: string | null) => {
    const idToUse = assetIdOverride ?? selectedMenuAssetId;
    if (!idToUse) return [];
    const multiSelectionActive = selectedAssetIds.length >= 2 && selectedIdSet.has(idToUse);
    return multiSelectionActive ? selectedAssetIds : [idToUse];
  }, [selectedAssetIds, selectedMenuAssetId]);

  const firstPendingAssetName = React.useMemo(() => {
    if (pendingCollectionAssetIds.length > 1) return '';
    const id = pendingCollectionAssetIds[0] || selectedMenuAssetId;
    if (!id) return '';
    if (selectedAssetForMenu && selectedAssetForMenu._id === id) {
      return selectedAssetForMenu.fileName;
    }
    const found = assets.find(asset => asset._id === id);
    return found?.fileName || selectedAssetForMenu?.fileName || '';
  }, [assets, pendingCollectionAssetIds, selectedAssetForMenu, selectedMenuAssetId]);

  // Image container dimensions handled via aspect-ratio; no explicit height needed

  // Scale-dependent spacings to preserve visual proportions at any size
  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
  // Inner padding around the image inside the gray container
  // Make padding proportional to thumbnail size so images scale consistently
  // Discrete thumbnail presets instead of slider
  const PRESETS = {
    MIN: { thumb: 140, fontTitle: 12, fontMeta: 10, badgeScale: 0.85 },
    MED: { thumb: 190, fontTitle: 13, fontMeta: 11, badgeScale: 0.95 },
    MAX: { thumb: 260, fontTitle: 15, fontMeta: 12, badgeScale: 1.0 }
  } as const;

  type ThumbPresetKey = keyof typeof PRESETS;
  const presetFromSize = (size: number): ThumbPresetKey => {
    if (size <= (PRESETS.MIN.thumb + PRESETS.MED.thumb) / 2) return 'MIN';
    if (size >= (PRESETS.MED.thumb + PRESETS.MAX.thumb) / 2) return 'MAX';
    return 'MED';
  };
  const activePreset: ThumbPresetKey = presetFromSize(thumbnailSize);
  const preset = PRESETS[activePreset];

  // Padding ska vara:
  // - lite STÖRRE vid minsta storlek
  // - lika med bas-padding från default och uppåt
  // Proportionell padding: behåll samma relativa luft som i default (210px)
  const basePad = 10;                 // padding vid default
  const proportionalPad = Math.round(basePad * (preset.thumb / 210));
  // Lite extra luft endast vid minsta läge
  const extraAtMin = 6;
  // Discrete presets: ge extra luft endast i MIN-läget
  const extraMinWeight = activePreset === 'MIN' ? 1 : 0; // 1 vid MIN, 0 annars
  const imageInnerPadding = proportionalPad + Math.round(extraAtMin * extraMinWeight);

  // Justera aspect ratio så stora thumbnails blir smalare (mindre sidofält) än default
  // - default: 5/6 (≈0.833)
  // - max:    0.68 (närmare höga produktbilder)
  // Kritisk fix: behåll EXAKT samma aspect-ratio i default och max
  // så att bildytan och padding skalar identiskt när rutan blir större
  const dynamicAspect = 5 / 6;
  
  // Text area padding scales proportionally with thumbnail size
  const textPadX = Math.round(imageInnerPadding * 1.2);
  const textPadTop = Math.round(imageInnerPadding * 1.2);
  const textPadBottom = Math.round(imageInnerPadding * 1.4);


  // Lägg till en effekt som fördröjer visningen av "inga assets hittades"
  useEffect(() => {
    // Om assets laddas, dölj meddelandet omedelbart
    if (loading) {
      setShowNoAssetsMessage(false);
      return;
    }
    
    // Om det finns assets, dölj meddelandet
    if (assets.length > 0) {
      setShowNoAssetsMessage(false);
      return;
    }
    
    // Om det inte finns assets och inte laddar, visa meddelandet efter fördröjning
    const timer = setTimeout(() => {
      setShowNoAssetsMessage(true);
    }, 1000); // Vänta 1 sekund innan meddelandet visas
    
    return () => clearTimeout(timer);
  }, [loading, assets.length]);

  // Hantera sorteringsändring
  const handleSortChange = (newSortOrder: string) => {
    userSelectedSortRef.current = true;
    setSortOrder(newSortOrder);
    onSortOrderChange(newSortOrder);
    setSortMenuAnchorEl(null);
    // Prevent accidental grid selections right after choosing a sort option
    suppressNextClicks();
  };

  // Öppna sorteringsmenyn
  const handleSortMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setSortMenuAnchorEl(event.currentTarget);
  };

  // Stäng sorteringsmenyn
  const handleSortMenuClose = () => {
    setSortMenuAnchorEl(null);
  };

  // Suppress accidental grid item clicks for a short window after toolbar actions
  const suppressClickRef = React.useRef<number>(0);
  const suppressNextClicks = () => { suppressClickRef.current = Date.now(); };

  // Handle asset click with multi-select support
  const handleAssetClick = useCallback((asset: IAsset, index: number, event: React.MouseEvent) => {
    if (Date.now() - suppressClickRef.current < 400) return;
    const isTabletSelectionMode = tabletToolbar && multiSelectEnabled;
    // Allow Ctrl/Shift multi även när multiSelectEnabled är false
    let newSelectedIds = [...selectedAssetIds];
    
    if ((event.shiftKey && lastSelectedIndex !== null)) {
      // Shift click - select range
      const start = Math.min(index, lastSelectedIndex);
      const end = Math.max(index, lastSelectedIndex);
      
      const idsInRange = assets.slice(start, end + 1).map(a => a._id);
      
      // Add all IDs in range that aren't already selected
      idsInRange.forEach(id => {
        if (!newSelectedIds.includes(id)) {
          newSelectedIds.push(id);
        }
      });
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd click - toggle selection
      if (newSelectedIds.includes(asset._id)) {
        newSelectedIds = newSelectedIds.filter(id => id !== asset._id);
      } else {
        newSelectedIds.push(asset._id);
      }
    } else {
      if (isTabletSelectionMode) {
        // Tablet markeringsläge: a normal tap toggles without opening details.
        if (newSelectedIds.includes(asset._id)) {
          newSelectedIds = newSelectedIds.filter(id => id !== asset._id);
        } else {
          newSelectedIds.push(asset._id);
        }
      } else if (multiSelectEnabled) {
        // Normal click in explicit desktop multi-mode – select only this item
        newSelectedIds = [asset._id];
      } else {
        // Normal single‑click in normal mode
        newSelectedIds = [asset._id];
      }
    }
    
    setLastSelectedIndex(index);
    onSelectedAssetsChange(newSelectedIds);
    onSelectionAnchorChange(newSelectedIds.includes(asset._id) ? asset._id : (newSelectedIds[newSelectedIds.length - 1] || null));
    
    // Also call the single select handler if only one asset is selected
    if (!isTabletSelectionMode && newSelectedIds.length === 1) {
      onAssetSelect(newSelectedIds[0]);
    }
  }, [assets, lastSelectedIndex, multiSelectEnabled, onAssetSelect, onSelectedAssetsChange, onSelectionAnchorChange, selectedAssetIds, tabletToolbar]);

  // Handle drag start for asset item
  const handleDragStart = useCallback((event: React.DragEvent, asset: IAsset) => {
    const assetsToDrag = selectedIdSet.has(asset._id) 
      ? selectedAssetIds 
      : [asset._id];
    event.dataTransfer.setData('application/json', JSON.stringify(assetsToDrag));
    event.dataTransfer.effectAllowed = 'copyMove';
  }, [selectedAssetIds, selectedIdSet]);

  const gridRef = React.useRef<HTMLDivElement|null>(null);

  const {
    isDraggingSelect, dragBoxRef,
    handleMouseDown, handleMouseMove, handleMouseUp,
  } = useDragSelection(gridRef, selectedAssetIds, onSelectedAssetsChange, onSelectionAnchorChange, suppressClickRef);

  const menuSelectionRef = React.useRef<string[]>([]);
  const skipMenuCloseResetRef = React.useRef(false);

  const statusFilterContext = isUnderProducts ? 'products' : isUnderWine ? 'wine' : 'other';
  const statusFilterContextRef = React.useRef(statusFilterContext);
  useEffect(() => {
    if (statusFilterContextRef.current === statusFilterContext) return;
    statusFilterContextRef.current = statusFilterContext;
    if (dabasStatusFilter !== 'all') {
      setDabasStatusFilter('all');
    }
  }, [statusFilterContext, dabasStatusFilter]);

  // When server kontor filter is active, keep local Validoo/Dabas filter at "all"
  useEffect(() => {
    if ((kontorFilter === 'true' || kontorFilter === 'false') && dabasStatusFilter !== 'all') {
      setDabasStatusFilter('all');
    }
  }, [kontorFilter, dabasStatusFilter]);

  const productsStatusSelectValue =
    kontorFilter === 'true' ? 'kontor'
      : kontorFilter === 'false' ? 'not_kontor'
        : dabasStatusFilter;

  const handleProductsStatusChange = (value: string) => {
    suppressNextClicks();
    if (value === 'kontor') {
      setDabasStatusFilter('all');
      onKontorFilterChange('true');
      return;
    }
    if (value === 'not_kontor') {
      setDabasStatusFilter('all');
      onKontorFilterChange('false');
      return;
    }
    if (kontorFilter !== 'all') {
      onKontorFilterChange('all');
    }
    setDabasStatusFilter(value as typeof dabasStatusFilter);
  };

  const normalizeMetaValue = (value: any): string => {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
      const first = value.map(v => String(v).trim()).find(v => v);
      return first || '';
    }
    return String(value).trim();
  };

  const getProductMetaLine = (asset: IAsset): string => {
    const artikelnummer = normalizeMetaValue(asset.metadata?.artikelnummer);
    const viktAntalValidoo = normalizeMetaValue(asset.metadata?.vikt_antal_validoo);

    if (artikelnummer && viktAntalValidoo) return `${artikelnummer} | ${viktAntalValidoo}`;
    return artikelnummer || viktAntalValidoo || '';
  };

  const getWineMetaLine = (asset: IAsset): string => {
    const artikelnummer = normalizeMetaValue((asset.metadata as any)?.artikelnummer_vin_id);
    const volym = normalizeMetaValue((asset.metadata as any)?.volym_vin_id);
    if (artikelnummer && volym) return `${artikelnummer} | ${volym}`;
    return artikelnummer || volym || '';
  };

  const getWineProductName = (asset: IAsset): string => {
    const meta = (asset.metadata as any) || {};
    const direct = normalizeMetaValue(meta?.Produktnamn_vin_id);
    if (direct) return direct;
    try {
      for (const [k, v] of Object.entries(meta)) {
        if (String(k).toLowerCase().includes('produktnamn_vin_id')) {
          const vv = normalizeMetaValue(v);
          if (vv) return vv;
        }
      }
    } catch {}
    return '';
  };

  const isAssetUnderRoot = useCallback((asset: IAsset, rootId: string | null): boolean => {
    if (!rootId || !Array.isArray(asset.categories) || asset.categories.length === 0) return false;

    const isDescendant = (categoryId: string): boolean => {
      let current: string | null | undefined = categoryId;
      const visited = new Set<string>();

      while (current && !visited.has(current)) {
        if (current === rootId) return true;
        visited.add(current);
        current = categoryParentById.get(current) || null;
      }

      return false;
    };

    return asset.categories
      .map(getCategoryRefId)
      .filter(Boolean)
      .some(categoryId => isDescendant(categoryId));
  }, [categoryParentById]);

  const isRecipeAsset = useCallback((asset: IAsset): boolean => {
    return isAssetUnderRoot(asset, recipesRootId);
  }, [isAssetUnderRoot, recipesRootId]);

  const isWineAsset = useCallback((asset: IAsset): boolean => {
    return isAssetUnderRoot(asset, wineRootId);
  }, [isAssetUnderRoot, wineRootId]);

  const isProductsAsset = useCallback((asset: IAsset): boolean => {
    return isAssetUnderRoot(asset, productsRootId);
  }, [isAssetUnderRoot, productsRootId]);

  const getRecipeName = (asset: IAsset): string => {
    const meta = (asset.metadata as any) || {};
    const direct = normalizeMetaValue(meta?.receptnamn_recept_id);
    if (direct) return direct;

    try {
      for (const [k, v] of Object.entries(meta)) {
        const kl = String(k).toLowerCase();
        if (kl === 'receptnamn_recept_id') {
          const vv = normalizeMetaValue(v);
          if (vv) return vv;
        }
      }
    } catch {}

    return '';
  };

  const getProductsProductName = (asset: IAsset): string => {
    const meta = (asset.metadata as any) || {};
    const direct = normalizeMetaValue(meta?.produktnamn) || normalizeMetaValue(meta?.Produktnamn);
    if (direct) return direct;
    try {
      for (const [k, v] of Object.entries(meta)) {
        const kl = String(k).toLowerCase();
        if (kl.includes('produktnamn') && !kl.includes('vin_id')) {
          const vv = normalizeMetaValue(v);
          if (vv) return vv;
        }
      }
    } catch {}
    return '';
  };

  const getCardTitle = (asset: IAsset): string => {
    if (isRecipeAsset(asset)) {
      const recipeName = getRecipeName(asset);
      if (recipeName) return recipeName;
    }
    if (isWineAsset(asset)) {
      const pn = getWineProductName(asset);
      if (pn) return pn;
    }
    if (isProductsAsset(asset)) {
      const pn = getProductsProductName(asset);
      if (pn) return pn;
    }
    const t = normalizeMetaValue((asset as any)?.title);
    if (t) return t;
    return decodeFileName(asset.fileName.split(/(\\|\/)/).pop() || asset.fileName);
  };

  const getArtikelnummerSortValue = (asset: IAsset): { num: number; raw: string } => {
    const raw = isUnderWine
      ? normalizeMetaValue((asset.metadata as any)?.artikelnummer_vin_id)
      : normalizeMetaValue(asset.metadata?.artikelnummer);
    if (!raw) return { num: Number.POSITIVE_INFINITY, raw: '' };
    const match = raw.match(/\d+/);
    const num = match ? Number(match[0]) : Number.POSITIVE_INFINITY;
    return { num, raw };
  };

  const compareSearchPriority = (a: IAsset, b: IAsset): number => {
    const aMatched = a.searchPriorityMatched === true;
    const bMatched = b.searchPriorityMatched === true;
    if (aMatched === bMatched) return 0;
    return aMatched ? -1 : 1;
  };

  const compareWordPressPublishedAt = (a: IAsset, b: IAsset, direction: 'asc' | 'desc'): number => {
    const priorityCmp = compareSearchPriority(a, b);
    if (priorityCmp !== 0) return priorityCmp;

    const aTime = a.wordpressPublishedAt ? new Date(a.wordpressPublishedAt).getTime() : NaN;
    const bTime = b.wordpressPublishedAt ? new Date(b.wordpressPublishedAt).getTime() : NaN;
    const aHasDate = Number.isFinite(aTime);
    const bHasDate = Number.isFinite(bTime);

    if (!aHasDate && !bHasDate) {
      return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
    }
    if (!aHasDate) return 1;
    if (!bHasDate) return -1;

    return direction === 'asc' ? aTime - bTime : bTime - aTime;
  };

  // Memoized sorted + filtered assets (only recomputed when dependencies change)
  const sortedAssets = React.useMemo(() => {
    if (!assets || assets.length === 0) return [];
    
    const assetsCopy = [...assets];
    const filteredByStoreType = storeTypeCategoryId
      ? assetsCopy.filter(a => Array.isArray((a as any).categories) && ((a as any).categories as any[]).some((cid: any) => String(cid) === storeTypeCategoryId))
      : assetsCopy;

    const filteredByDabasStatus = dabasStatusFilter === 'all'
      ? filteredByStoreType
      : isUnderProducts
        ? filteredByStoreType.filter(a => {
            if (dabasStatusFilter === 'validoo_approved') return getValidooImageStatusState(a) === 'approved';
            if (dabasStatusFilter === 'validoo_rejected') return getValidooImageStatusState(a) === 'rejected';
            if (dabasStatusFilter === 'validoo_unchecked') return getValidooImageStatusState(a) === 'unchecked';
            if (dabasStatusFilter === 'failed') return a.dabasSyncStatus === 'failed' && !a.skipDabasSync && !a.hasPreviousDabasSync;
            if (dabasStatusFilter === 'failed_previous') return a.dabasSyncStatus === 'failed' && !a.skipDabasSync && !!a.hasPreviousDabasSync;
            if (dabasStatusFilter === 'success') return a.dabasSyncStatus === 'success' && !a.skipDabasSync;
            if (dabasStatusFilter === 'skip') return !!a.skipDabasSync;
            return true;
          })
        : isUnderWine
          ? filteredByStoreType.filter(a => {
              if (dabasStatusFilter === 'validoo_approved') return getValidooImageStatusState(a) === 'approved';
              if (dabasStatusFilter === 'validoo_rejected') return getValidooImageStatusState(a) === 'rejected';
              if (dabasStatusFilter === 'validoo_unchecked') return getValidooImageStatusState(a) === 'unchecked';
              return true;
            })
          : filteredByStoreType;
    
    switch (sortOrder) {
      case 'newest':
        return filteredByDabasStatus.sort((a, b) => {
          const priorityCmp = compareSearchPriority(a, b);
          if (priorityCmp !== 0) return priorityCmp;
          return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
        });
      case 'oldest':
        return filteredByDabasStatus.sort((a, b) => {
          const priorityCmp = compareSearchPriority(a, b);
          if (priorityCmp !== 0) return priorityCmp;
          return new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime();
        });
      case 'wp_newest':
        if (!showWordPressPublishedSort) return filteredByDabasStatus;
        return filteredByDabasStatus.sort((a, b) => compareWordPressPublishedAt(a, b, 'desc'));
      case 'wp_oldest':
        if (!showWordPressPublishedSort) return filteredByDabasStatus;
        return filteredByDabasStatus.sort((a, b) => compareWordPressPublishedAt(a, b, 'asc'));
      case 'name_asc':
        return filteredByDabasStatus.sort((a, b) => {
          const priorityCmp = compareSearchPriority(a, b);
          if (priorityCmp !== 0) return priorityCmp;
          return a.fileName.localeCompare(b.fileName);
        });
      case 'name_desc':
        return filteredByDabasStatus.sort((a, b) => {
          const priorityCmp = compareSearchPriority(a, b);
          if (priorityCmp !== 0) return priorityCmp;
          return b.fileName.localeCompare(a.fileName);
        });
      case 'artikelnummer_asc':
        if (!isUnderProducts && !isUnderWine) return filteredByDabasStatus;
        return filteredByDabasStatus.sort((a, b) => {
          const priorityCmp = compareSearchPriority(a, b);
          if (priorityCmp !== 0) return priorityCmp;
          const av = getArtikelnummerSortValue(a);
          const bv = getArtikelnummerSortValue(b);
          const aHas = Number.isFinite(av.num);
          const bHas = Number.isFinite(bv.num);
          if (!aHas && !bHas) {
            const rawCmp = av.raw.localeCompare(bv.raw, 'sv', { numeric: true });
            return rawCmp !== 0 ? rawCmp : a.fileName.localeCompare(b.fileName);
          }
          if (!aHas) return 1;
          if (!bHas) return -1;
          if (av.num !== bv.num) return av.num - bv.num;
          return av.raw.localeCompare(bv.raw, 'sv', { numeric: true });
        });
      default:
        return filteredByDabasStatus;
    }
  }, [assets, sortOrder, storeTypeCategoryId, dabasStatusFilter, isUnderProducts, isUnderWine, showWordPressPublishedSort]);

  // Default sort only when entering/leaving Products or Wine.
  // Important: do NOT override when the user explicitly picks "Nyast först".
  const wasUnderProductsOrWineRef = React.useRef<boolean>(false);
  useEffect(() => {
    const isUnderProductsOrWine = isUnderProducts || isUnderWine;
    const wasUnderProductsOrWine = wasUnderProductsOrWineRef.current;

    if (isUnderProductsOrWine && !wasUnderProductsOrWine) {
      // Entering Products/Wine: default to artikelnummer only if still at default "newest"
      if (sortOrder === 'newest') setSortOrder('artikelnummer_asc');
    } else if (!isUnderProductsOrWine && wasUnderProductsOrWine) {
      // Leaving Products/Wine: reset to newest only if we were on artikelnummer sort
      if (sortOrder === 'artikelnummer_asc') setSortOrder('newest');
    }

    wasUnderProductsOrWineRef.current = isUnderProductsOrWine;
  }, [isUnderProducts, isUnderWine, sortOrder]);

  useEffect(() => {
    if (!showWordPressPublishedSort && (sortOrder === 'wp_newest' || sortOrder === 'wp_oldest')) {
      setSortOrder('newest');
      onSortOrderChange('newest');
    }
  }, [showWordPressPublishedSort, sortOrder, onSortOrderChange]);

  // Receptvyn ska defaulta till WordPress-publiceringsdatum nyast först.
  const wasUnderRecipesRef = React.useRef<boolean>(false);
  useEffect(() => {
    if (showWordPressPublishedSort && !wasUnderRecipesRef.current) {
      userSelectedSortRef.current = false;
    }

    if (showWordPressPublishedSort && !userSelectedSortRef.current && sortOrder === 'newest') {
      setSortOrder('wp_newest');
      onSortOrderChange('wp_newest');
    }

    if (!showWordPressPublishedSort && wasUnderRecipesRef.current) {
      userSelectedSortRef.current = false;
    }

    wasUnderRecipesRef.current = showWordPressPublishedSort;
  }, [showWordPressPublishedSort, sortOrder, onSortOrderChange]);

  // Determine PDF template type based on current category context
  const pdfTemplateType = React.useMemo<'products' | 'wine' | null>(() => {
    if (isUnderProducts) return 'products';
    if (isUnderWine) return 'wine';
    return null;
  }, [isUnderProducts, isUnderWine]);

  const visiblePdfTemplates = React.useMemo(() => {
    return filterTemplatesForSelection(pdfTemplates || [], selectedAssetIds.length);
  }, [pdfTemplates, selectedAssetIds]);

  // Load PDF templates when menu opens
  const loadPdfTemplates = React.useCallback(async () => {
    setLoadingPdfTemplates(true);
    try {
      const templates = await getActivePdfTemplates(pdfTemplateType || undefined);
      setPdfTemplates(templates);
    } catch (err) {
      console.error('Failed to load PDF templates:', err);
      setPdfTemplates([]);
    } finally {
      setLoadingPdfTemplates(false);
    }
  }, [pdfTemplateType]);

  // Handle PDF download for selected asset(s)
  const handlePdfDownload = React.useCallback(async (templateId: string) => {
    const selectedSet = new Set(selectedAssetIds);
    const assetsToDownload = selectedSet.size > 0 
      ? assets.filter(a => selectedSet.has(a._id))
      : selectedAssetForMenu 
        ? [selectedAssetForMenu]
        : [];
    
    if (assetsToDownload.length === 0) return;
    
    setDownloadingPdf(true);
    try {
      const tpl = pdfTemplates.find(t => t._id === templateId);
      const assetIds = assetsToDownload.map(a => a._id);

      // Multi-product template => generate ONE PDF for all selected assets
      if (tpl?.templateMode === 'multi') {
        if ((tpl as any).maxProducts && assetIds.length > (tpl as any).maxProducts) {
          alert(`Den valda PDF-mallen stödjer högst ${(tpl as any).maxProducts} produkter.`);
          return;
        }
        if (assetIds.length > 20) {
          alert('Max 20 produkter kan genereras i en multi-PDF just nu.');
          return;
        }
        await downloadMultiPdf(templateId, assetIds);
      } else {
        // Single-product template => generate one PDF per asset
        for (const asset of assetsToDownload) {
          await downloadPdf(templateId, asset._id);
        }
      }
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      alert(err.message || 'Kunde inte generera PDF');
    } finally {
      setDownloadingPdf(false);
      // Close menu
      setMenuAnchorEl(null);
      setSelectedAssetForMenu(null);
    }
  }, [selectedAssetIds, assets, selectedAssetForMenu, pdfTemplates]);

  // Build chain filter options from the explicit Produkter template flag.
  const chainFields = React.useMemo(() => {
    const productsTemplate = (templates as ITemplate[]).find(
      (template) => String(template?.name || '').trim().toLowerCase() === 'produkter',
    );

    if (!productsTemplate) {
      return [];
    }

    const finalFields = [...(productsTemplate.fields || [])]
      .filter((field) => field && field.type !== 'separator' && field.showInChainDropdown === true)
      .sort((a, b) => {
        const orderA = a.order === undefined ? Number.MAX_SAFE_INTEGER : a.order;
        const orderB = b.order === undefined ? Number.MAX_SAFE_INTEGER : b.order;
        if (orderA === orderB) {
          return String(a.label || '').localeCompare(String(b.label || ''));
        }
        return orderA - orderB;
      })
      .reduce<Array<{ id: string; label: string }>>((acc, field) => {
        const id = String(field.id || field.name || '').trim();
        const label = String(field.label || id).trim();
        if (!id || acc.some((item) => item.id === id)) {
          return acc;
        }
        acc.push({ id, label });
        return acc;
      }, []);

    finalFields.unshift({ id: '__FINNISH_PRODUCTS__', label: 'FINSKA PRODUKTER' });
    return finalFields;
  }, [templates]);

  const wineDropdownFields = React.useMemo(() => {
    const matchingWineTemplates = (templates as ITemplate[])
      .filter((template) =>
        (template.categoryIds || []).some((categoryId) =>
          isCategoryIdUnderRoot(String(categoryId || ''), wineRootId, categoryParentById),
        ),
      )
      .sort((a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)));

    const wineTemplate = matchingWineTemplates[0];
    if (!wineTemplate) {
      return [];
    }

    return [...(wineTemplate.fields || [])]
      .filter((field) => field && field.type !== 'separator' && field.showInWineDropdown === true)
      .sort((a, b) => {
        const orderA = a.order === undefined ? Number.MAX_SAFE_INTEGER : a.order;
        const orderB = b.order === undefined ? Number.MAX_SAFE_INTEGER : b.order;
        if (orderA === orderB) {
          return String(a.label || '').localeCompare(String(b.label || ''));
        }
        return orderA - orderB;
      })
      .reduce<Array<{ id: string; label: string }>>((acc, field) => {
        const id = String(field.id || field.name || '').trim();
        const label = String(field.label || id).trim();
        if (!id || acc.some((item) => item.id === id)) {
          return acc;
        }
        acc.push({ id, label });
        return acc;
      }, []);
  }, [templates, wineRootId, categoryParentById]);


  // Custom download templates used in the asset download menu
  const [activeDownloadTemplates, setActiveDownloadTemplates] = useState<ActiveDownloadTemplate[]>([]);
  const [loadingDownloadTemplates, setLoadingDownloadTemplates] = useState(false);
  const [downloadTemplatesError, setDownloadTemplatesError] = useState<string | null>(null);
  const {
    isSaving: isSavingForValidoo,
    errorInfo: validooErrorInfo,
    successResult: validooSuccessResult,
    closeErrorDialog: closeValidooErrorDialog,
    closeSuccessDialog: closeValidooSuccessDialog,
    saveForValidoo,
  } = useValidooExport();
  
  const fetchActiveDownloadTemplates = useCallback(async () => {
    setLoadingDownloadTemplates(true);
    setDownloadTemplatesError(null);
    try {
      const data = await fetchActiveImageDownloadTemplates();
      setActiveDownloadTemplates(data);
    } catch (error: any) {
      setActiveDownloadTemplates([]);
      setDownloadTemplatesError(error?.message || 'Kunde inte ladda egna templates.');
    } finally {
      setLoadingDownloadTemplates(false);
    }
  }, []);

  // Handle menu open - optimized for instant response
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, asset: IAsset) => {
    event.stopPropagation(); // Prevent asset selection
    
    // Set menu state immediately for instant UI response
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssetForMenu(asset);
    
    // Remember current selection so we can restore after menu actions
    try { menuSelectionRef.current = [...selectedAssetIds]; } catch { menuSelectionRef.current = []; }
    
    // Fetch templates in background (cached after first load)
    fetchActiveDownloadTemplates();
    
    // Fetch PDF templates if under Produkter or Vin
    if (isUnderProducts || isUnderWine) {
      loadPdfTemplates();
    }
  }, [selectedAssetIds, fetchActiveDownloadTemplates, isUnderProducts, isUnderWine, loadPdfTemplates]);

  // Handle menu close - memoized for performance
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    if (!addToCollectionDialogOpen && !skipMenuCloseResetRef.current) {
      setSelectedAssetForMenu(null);
    }
    // Restore selection to what it was when menu opened to avoid accidental changes
    if (menuSelectionRef.current && menuSelectionRef.current.length >= 0) {
      onSelectedAssetsChange(menuSelectionRef.current);
    }
    skipMenuCloseResetRef.current = false;
  }, [addToCollectionDialogOpen, onSelectedAssetsChange]);

  const getAssetsForMenuDownload = useCallback((): IAsset[] => {
    if (!selectedAssetForMenu) return [];

    const multiSelectionActive =
      selectedAssetIds.length >= 2 && selectedIdSet.has(selectedAssetForMenu._id);
    const idsToDownload = multiSelectionActive
      ? selectedAssetIds
      : [selectedAssetForMenu._id];

    return idsToDownload
      .map((id) => {
        if (id === selectedAssetForMenu._id) return selectedAssetForMenu;
        return assets.find((asset) => asset._id === id);
      })
      .filter(Boolean) as IAsset[];
  }, [assets, selectedAssetForMenu, selectedAssetIds, selectedIdSet]);

  const archiveAssetsDirectly = async (targetIds: string[]) => {
    if (targetIds.length === 0) return;

    await Promise.all(targetIds.map(id => axiosInstance.put(`/api/assets/${id}`, { archived: true })));
    setSnackbarMessage(targetIds.length > 1 ? `${targetIds.length} bilder har arkiverats` : 'Bilden har arkiverats');
    setSnackbarOpen(true);
    try {
      targetIds.forEach(id => {
        window.dispatchEvent(new CustomEvent('dam:asset-archived', { detail: { id } }));
      });
    } catch {}
  };

  const handleArchive = async () => {
    const targetIds = getAssetIdsForCurrentSelection();
    if (targetIds.length === 0) return;

    const targetAssets = targetIds
      .map((id) => {
        if (selectedAssetForMenu?._id === id) return selectedAssetForMenu;
        return assets.find((asset) => asset._id === id);
      })
      .filter(Boolean) as IAsset[];

    const shouldUseRelationImpactDialog = targetAssets.some((targetAsset) => (
      isAssetUnderNamedCategoryRoot(targetAsset, allCategories, 'recept')
      || isAssetUnderNamedCategoryRoot(targetAsset, allCategories, 'produkter')
    ));

    let wordPressImpacts: WordPressArchiveUnpublishImpact[] = [];
    try {
      wordPressImpacts = await getWordPressArchiveUnpublishImpacts(targetIds);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Kunde inte kontrollera WordPress-publicering.');
      handleMenuClose();
      return;
    }

    if (shouldUseRelationImpactDialog || wordPressImpacts.length > 0) {
      setRelationImpactAssets(targetAssets);
      setRelationImpactAssetIds(targetIds);
      setWordPressArchiveImpacts(wordPressImpacts);
      setRelationImpactDialogOpen(true);
      handleMenuClose();
      return;
    }

    try {
      await archiveAssetsDirectly(targetIds);
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Kunde inte arkivera.');
    } finally {
      handleMenuClose();
    }
  };

  const handleRelationArchiveConfirm = async () => {
    if (relationImpactAssetIds.length === 0) return;

    try {
      await archiveAssetsDirectly(relationImpactAssetIds);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Kunde inte arkivera.');
      throw error;
    }
  };

  const handleRelationArchiveConfirmWithWordPressUnpublish = async () => {
    if (relationImpactAssetIds.length === 0) return;

    try {
      await requestWordPressArchiveUnpublish(wordPressArchiveImpacts);
      await archiveAssetsDirectly(relationImpactAssetIds);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Kunde inte avpublicera och arkivera.');
      throw error;
    }
  };

  // Toggle kontor flag for one or multiple selected assets
  const handleToggleKontor = async () => {
    if (!selectedAssetForMenu) return;
    try {
      const ids = selectedAssetIds.length >= 2 ? selectedAssetIds : [selectedAssetForMenu._id];
      const selected = (selectedAssetIds.length >= 2)
        ? assets.filter(a => ids.includes(a._id))
        : [selectedAssetForMenu];
      const allKontor = selected.length > 0 && selected.every(a => isKontor(a) === true);
      const newValue = !allKontor;
      if (ids.length > 1) {
        await axiosInstance.post(`/api/assets/batch/kontor`, { assetIds: ids, kontor: newValue });
      } else {
        await axiosInstance.put(`/api/assets/${ids[0]}`, { kontor: newValue });
      }
      // Optimistically reflect in local UI
      setKontorOverrides(prev => {
        const next = { ...prev };
        ids.forEach(id => { next[id] = newValue; });
        return next;
      });
      setSnackbarMessage(newValue ? 'Markerad som Kontor' : 'Kontor borttagen');
      setSnackbarOpen(true);
      // Optionally notify app to refresh
      try { window.dispatchEvent(new CustomEvent('dam:assets-updated')); } catch {}
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Kunde inte uppdatera Kontor-flagga.');
    } finally {
      handleMenuClose();
    }
  };

  // Handle download with template - supports multiple selected assets
  const handleDownloadWithTemplate = async (templateId: BuiltInDownloadTemplateId) => {
    if (!selectedAssetForMenu) return;
    const assetsToDownload = getAssetsForMenuDownload();

    try {
      await downloadAssetsWithBuiltInTemplate(assetsToDownload, templateId);
      handleMenuClose();
    } catch (err: any) {
      console.error('Error downloading file(s):', err);
      alert(`Error downloading file(s): ${err.message}`);
      handleMenuClose();
    }
  };

  const handleRunCustomTemplate = async (downloadTemplateId: string) => {
    if (!selectedAssetForMenu) return;
    try {
      const assetsToDownload = getAssetsForMenuDownload();
      await runCustomAssetDownloadTemplate({
        templateId: downloadTemplateId,
        assetIds: assetsToDownload.map((item) => item._id),
        primaryAssetFileName: selectedAssetForMenu.fileName,
      });
      handleMenuClose();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Kunde inte ladda ned med template.');
    }
  };

  const handleSaveForValidoo = async () => {
    if (!selectedAssetForMenu) return;

    const result = await saveForValidoo(selectedAssetForMenu._id);
    handleMenuClose();
  };

  // Handle sharing
  const handleShare = () => {
    if (!selectedAssetForMenu) return;
    
    // Simple sharing function that copies URL to clipboard
    const shareUrl = `${window.location.origin}/asset/${selectedAssetForMenu._id}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setSnackbarMessage('Länk kopierad!');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Kunde inte kopiera: ', err);
        setSnackbarMessage('Kunde inte kopiera länk');
        setSnackbarOpen(true);
      });
    
    // Close menu
    handleMenuClose();
  };

  // Handle adding to collection
  const handleAddToCollectionClick = (event: React.MouseEvent<HTMLLIElement>, asset: IAsset) => {
    event.stopPropagation();
    const targetIds = getAssetIdsForCurrentSelection(asset._id);
    if (targetIds.length === 0) return;
    setPendingCollectionAssetIds(targetIds);
    setSelectedAssetForMenu(asset);
    setAddToCollectionDialogOpen(true);
    skipMenuCloseResetRef.current = true;
    setMenuAnchorEl(null);
  };

  const handleAddToCollection = () => handleAddToCollectionCore(() => getAssetIdsForCurrentSelection());

  const handleAddToCollectionDialogClose = () => {
    resetCollectionDialog();
    setSelectedAssetForMenu(null);
    skipMenuCloseResetRef.current = false;
  };

  // Function to render list view
  const renderListView = () => {
    return (
      <div style={LIST_CONTAINER_STYLE}>
        {sortedAssets.map((asset, index) => (
          <div 
            key={asset._id} 
            style={{ 
              display: 'flex',
              flexDirection: 'row',
              padding: '0',
              border: selectedIdSet.has(asset._id) ? '2px solid #072f53' : '1px solid #d4d4d4',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: 'white',
              boxShadow: selectedIdSet.has(asset._id) 
                ? '0 0 8px rgba(7, 47, 83, 0.5)' 
                : 'none',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              transform: selectedIdSet.has(asset._id) ? 'translateY(-2px)' : 'translateY(0)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '80px'
            }}
            onClick={(e) => handleAssetClick(asset, index, e)}
            draggable
            onDragStart={(e) => handleDragStart(e, asset)}
            onMouseOver={(e) => {
              if (!selectedIdSet.has(asset._id)) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }
            }}
            onMouseOut={(e) => {
              if (!selectedIdSet.has(asset._id)) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {/* Thumbnail area for list view */}
            <div style={LIST_THUMB_CONTAINER_STYLE}>
              {/* Duplicate indicator for list view */}
              {duplicateAssetIds.has(asset._id) && (
                <Tooltip title="Denna bild har dubbletter - klicka för att hantera">
                  <div
                    onClick={(e) => handleDuplicateIndicatorClick(e, asset._id)}
                    style={DUPLICATE_TRIANGLE_LIST_STYLE}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '-19px',
                      left: '3px',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>!</span>
                  </div>
                </Tooltip>
              )}
              {asset.previewPaths?.thumbnail ? (
                <img 
                  src={`${API_BASE_URL}${asset.previewPaths.thumbnail}${asset.modifiedAt || asset.uploadedAt ? `?v=${new Date(asset.modifiedAt || asset.uploadedAt).getTime()}` : ''}`}
                  alt={asset.fileName}
                  style={LIST_THUMB_IMG_STYLE}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.dataset.fallbackTried === '1') {
                      target.onerror = null;
                      return;
                    }
                    target.dataset.fallbackTried = '1';
                    const token = localStorage.getItem('authToken');
                    target.src = `${API_BASE_URL}/api/assets/${asset._id}/preview/thumbnail${token ? `?token=${encodeURIComponent(token)}` : ''}`;
                  }}
                />
              ) : asset.fileType.startsWith('image/') ? (
                <img 
                  src={`${API_BASE_URL}/${asset.filePath.replace(/\\/g, '/')}`}
                  alt={asset.fileName}
                  loading="lazy"
                  decoding="async"
                  style={LIST_THUMB_IMG_STYLE}
                />
              ) : asset.fileType.startsWith('video/') && asset.previewPaths?.thumbnail ? (
                <img 
                  src={`${API_BASE_URL}${asset.previewPaths.thumbnail}`}
                  alt={asset.fileName}
                  loading="lazy"
                  decoding="async"
                  style={LIST_THUMB_IMG_STYLE}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  height: '100%',
                  width: '100%'
                }}>
                  <div style={{ fontSize: '24px', opacity: 0.7 }}>
                    {asset.fileType.includes('pdf') ? '📄' : 
                     asset.fileType.includes('video') ? '🎬' :
                     asset.fileType.includes('audio') ? '🎵' : 
                     asset.fileType.includes('postscript') || asset.fileName.toLowerCase().endsWith('.ai') ? '🎨' : '📁'}
                  </div>
                </div>
              )}
              
              {/* Selection and favorite indicators for list view */}
              {selectedIdSet.has(asset._id) && (
                <span 
                  style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    color: '#072f53',
                    fontSize: '1.2em',
                    textShadow: '0 0 3px rgba(255,255,255,0.7)'
                  }}
                >
                  ✓
                </span>
              )}
              
              {asset.isFavorite && (
                <span 
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    color: 'gold',
                    fontSize: '1.2em',
                    textShadow: '0 0 3px rgba(0,0,0,0.5)'
                  }}
                >
                  ★
                </span>
              )}

              {/* Store Material Stock Indicator for list view */}
              {storeAssetIds.has(asset._id) && (
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: stockLevels[asset._id] !== undefined ? getStockColor(stockLevels[asset._id]) : '#ccc',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7em',
                    fontWeight: 'bold',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    zIndex: 3
                  }}
                >
                  {stockLevelsLoading ? '...' : (stockLevels[asset._id] || 0)}
                </div>
              )}
            </div>

            {/* Content area for list view */}
            <div style={{ 
              flexGrow: 1,
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '80px'
            }}>
              {/* Title (List view) */}
              <Typography variant="body1" style={{ 
                fontWeight: 'bold',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {getCardTitle(asset)}
              </Typography>

              {/* File info and categories */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {/* File dimensions and size */}
                {asset.width && asset.height && (
                  <Typography variant="body2" style={{ color: '#666' }}>
                    {asset.width} × {asset.height} px
                    {asset.fileSize > 0 && (
                      <>
                        {' | '}
                        {formatFileSize(asset.fileSize)}
                        {isKontor(asset) && (
                          <>
                            {' | '}
                            <Tooltip title="Kontoret" arrow>
                              <LensIcon sx={{ color: '#666', fontSize: '0.9em', verticalAlign: 'middle' }} />
                            </Tooltip>
                          </>
                        )}
                      </>
                    )}
                  </Typography>
                )}
                
                {/* Categories */}
                {asset.categories && asset.categories.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {asset.categories.slice(0, 3).map(categoryId => {
                      const category = categoryById.get(categoryId);
                      return category ? (
                        <Chip 
                          key={categoryId} 
                          label={category.name}
                          size="small"
                          variant="outlined"
                          style={{ 
                            fontSize: '0.75em',
                            height: '20px'
                          }}
                        />
                      ) : null;
                    })}
                    {asset.categories.length > 3 && (
                      <Typography variant="body2" style={{ color: '#999', fontSize: '0.8em' }}>
                        +{asset.categories.length - 3} fler
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions area for list view */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              flexShrink: 0
            }}>
              {/* Store material "Beställ" button */}
              {storeAssetIds.has(asset._id) && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    addToCart(asset._id);
                  }}
                  disabled={stockLevels[asset._id] === 0}
                  startIcon={<AddShoppingCartIcon />}
                  sx={{
                    backgroundColor: '#072f53',
                    color: 'white',
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: '#051a2e'
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#666'
                    }
                  }}
                >
                  Beställ
                </Button>
              )}

              {/* Menu button */}
              <IconButton 
                size="small" 
                onClick={(e) => handleMenuOpen(e, asset)}
                sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  '&:hover': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.1)' 
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Visa fel endast om vi inte har någon data alls (första laddning).
  // Vid transient nätverksfail under refetch vill vi behålla gridden och undvika "blink".
  if (error && assets.length === 0) {
    return <div>Error loading assets: {error.message}</div>;
  }

  const visibleAssets = sortedAssets;
  const stickyToolbarTop = activeCollectionId ? '48px' : '0px';

  return (
    <div ref={gridRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} className="asset-grid" style={{ 
      backgroundColor: '#f8f8f8',
      padding: compactToolbar ? '8px 10px' : '16px',
      borderRadius: compactToolbar ? '4px' : '8px',
      width: '100%'
    }}>
      {/* Sorteringskontroll - placerad före grid */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: compactToolbar ? '0 0 6px 0' : isTabletToolbar ? '0 0 10px 0' : '0 0 16px 0',
        marginBottom: compactToolbar ? '4px' : isTabletToolbar ? '6px' : '8px',
        gap: compactToolbar ? '6px' : isTabletToolbar ? '10px' : '16px',
        flexWrap: isTabletToolbar ? 'nowrap' : 'wrap',
        justifyContent: isTabletToolbar ? 'flex-start' : 'center',
        position: 'sticky',
        top: stickyToolbarTop,
        zIndex: 5,
        background: '#f8f8f8',
        overflowX: isTabletToolbar ? 'auto' : 'visible',
        overflowY: 'hidden'
      }} onMouseDown={(e)=>{ /* avoid starting drag-select from toolbar */ e.stopPropagation(); }}>
        {/* Sorteringsknapp */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: '#f8f8f8', 
          padding: compactToolbar ? '4px 8px' : '8px 12px', 
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          height: compactToolbar ? '30px' : '40px',
          order: 0
        }} onClick={handleSortMenuOpen}>
          <SortIcon fontSize="small" style={{ marginRight: '4px', color: '#555' }} />
          <Typography variant="body2" style={{ color: '#555', fontWeight: 500, fontSize: compactToolbar ? '0.7rem' : undefined }}>
            {sortOrder === 'newest' ? 'Nyast först' : 
             sortOrder === 'oldest' ? 'Äldst först' :
             sortOrder === 'wp_newest' ? 'WP nyast' :
             sortOrder === 'wp_oldest' ? 'WP äldst' :
             sortOrder === 'name_asc' ? 'A-Ö' :
             sortOrder === 'name_desc' ? 'Ö-A' :
             sortOrder === 'artikelnummer_asc' ? 'Artikelnummer (0-9)' : 'Sortera'}
          </Typography>
        </div>

        {/* Status filter dropdown (endast under Produkter) */}
        {isUnderProducts && (
          <FormControl size="small" sx={{ minWidth: 190, order: 0 }}>
            <InputLabel id="dabas-status-filter-label">Status</InputLabel>
            <Select
              labelId="dabas-status-filter-label"
              label="Status"
              value={productsStatusSelectValue}
              onOpen={() => { suppressNextClicks(); }}
              onClose={() => { suppressNextClicks(); }}
              onChange={(e) => { handleProductsStatusChange(String(e.target.value)); }}
              sx={{ height: '40px', backgroundColor: '#f8f8f8', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
              renderValue={(val) => {
                if (val === 'all') return 'Alla';
                if (val === 'validoo_approved') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('approved', 16)} Bild godkänd för Validoo</span>;
                if (val === 'validoo_rejected') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('rejected', 16)} Bild ej godkänd för Validoo</span>;
                if (val === 'validoo_unchecked') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('unchecked', 16)} Bild ej kontrollerad för Validoo</span>;
                if (val === 'failed') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CancelIcon sx={{ color: '#f44336', fontSize: 16 }} /> Aldrig synkad</span>;
                if (val === 'failed_previous') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CancelIcon sx={{ color: '#ff9800', fontSize: 16 }} /> Tappat synk</span>;
                if (val === 'success') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} /> Lyckad synk</span>;
                if (val === 'skip') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><SyncDisabledIcon sx={{ color: '#8a8a8a', fontSize: 16 }} /> Synkar ej</span>;
                if (val === 'kontor') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><LensIcon sx={{ color: '#888', fontSize: 14 }} /> Kontor</span>;
                if (val === 'not_kontor') return 'Inte kontor';
                return 'Alla';
              }}
            >
              <MenuItem value="all"><em>Alla</em></MenuItem>
              <MenuItem value="validoo_approved">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('approved', 18)}</ListItemIcon>
                <ListItemText primary="Bild godkänd för Validoo" />
              </MenuItem>
              <MenuItem value="validoo_rejected">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('rejected', 18)}</ListItemIcon>
                <ListItemText primary="Bild ej godkänd för Validoo" />
              </MenuItem>
              <MenuItem value="validoo_unchecked">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('unchecked', 18)}</ListItemIcon>
                <ListItemText primary="Bild ej kontrollerad för Validoo" />
              </MenuItem>
              <MenuItem value="success">
                <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} /></ListItemIcon>
                <ListItemText primary="Lyckad synk" />
              </MenuItem>
              <MenuItem value="failed_previous">
                <ListItemIcon sx={{ minWidth: 28 }}><CancelIcon sx={{ color: '#ff9800', fontSize: 18 }} /></ListItemIcon>
                <ListItemText primary="Tappat synk" />
              </MenuItem>
              <MenuItem value="failed">
                <ListItemIcon sx={{ minWidth: 28 }}><CancelIcon sx={{ color: '#f44336', fontSize: 18 }} /></ListItemIcon>
                <ListItemText primary="Aldrig synkad" />
              </MenuItem>
              <MenuItem value="skip">
                <ListItemIcon sx={{ minWidth: 28 }}><SyncDisabledIcon sx={{ color: '#8a8a8a', fontSize: 18 }} /></ListItemIcon>
                <ListItemText primary="Synkar ej" />
              </MenuItem>
              {canFilterKontorProducts && (
                <MenuItem value="kontor">
                  <ListItemIcon sx={{ minWidth: 28 }}><LensIcon sx={{ color: '#888', fontSize: 16 }} /></ListItemIcon>
                  <ListItemText primary="Kontor" />
                </MenuItem>
              )}
              {canFilterKontorProducts && (
                <MenuItem value="not_kontor">
                  <ListItemText primary="Inte kontor" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {/* Status filter dropdown (Vin: Validoo och kontor) */}
        {isUnderWine && !isUnderProducts && (
          <FormControl size="small" sx={{ minWidth: 190, order: 0 }}>
            <InputLabel id="wine-status-filter-label">Status</InputLabel>
            <Select
              labelId="wine-status-filter-label"
              label="Status"
              value={
                kontorFilter === 'true' ? 'kontor'
                  : kontorFilter === 'false' ? 'not_kontor'
                    : (dabasStatusFilter === 'validoo_approved' || dabasStatusFilter === 'validoo_rejected' || dabasStatusFilter === 'validoo_unchecked')
                      ? dabasStatusFilter
                      : 'all'
              }
              onOpen={() => { suppressNextClicks(); }}
              onClose={() => { suppressNextClicks(); }}
              onChange={(e) => { handleProductsStatusChange(String(e.target.value)); }}
              sx={{ height: '40px', backgroundColor: '#f8f8f8', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
              renderValue={(val) => {
                if (val === 'validoo_approved') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('approved', 16)} Bild godkänd för Validoo</span>;
                if (val === 'validoo_rejected') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('rejected', 16)} Bild ej godkänd för Validoo</span>;
                if (val === 'validoo_unchecked') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{renderValidooStatusGlyph('unchecked', 16)} Bild ej kontrollerad för Validoo</span>;
                if (val === 'kontor') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><LensIcon sx={{ color: '#888', fontSize: 14 }} /> Kontor</span>;
                if (val === 'not_kontor') return 'Inte kontor';
                return 'Alla';
              }}
            >
              <MenuItem value="all"><em>Alla</em></MenuItem>
              <MenuItem value="validoo_approved">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('approved', 18)}</ListItemIcon>
                <ListItemText primary="Bild godkänd för Validoo" />
              </MenuItem>
              <MenuItem value="validoo_rejected">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('rejected', 18)}</ListItemIcon>
                <ListItemText primary="Bild ej godkänd för Validoo" />
              </MenuItem>
              <MenuItem value="validoo_unchecked">
                <ListItemIcon sx={{ minWidth: 28 }}>{renderValidooStatusGlyph('unchecked', 18)}</ListItemIcon>
                <ListItemText primary="Bild ej kontrollerad för Validoo" />
              </MenuItem>
              {canFilterKontorWine && (
                <MenuItem value="kontor">
                  <ListItemIcon sx={{ minWidth: 28 }}><LensIcon sx={{ color: '#888', fontSize: 16 }} /></ListItemIcon>
                  <ListItemText primary="Kontor" />
                </MenuItem>
              )}
              {canFilterKontorWine && (
                <MenuItem value="not_kontor">
                  <ListItemText primary="Inte kontor" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {/* Select All / Deselect Toggle Button — hidden in compact toolbar */}
        {!compactToolbar && (
        <Button
          size="small"
          variant={selectedAssetIds.length > 0 ? "contained" : "outlined"}
          onClick={() => {
            if (selectedAssetIds.length > 0) {
              // Deselect all
              onSelectedAssetsChange([]);
              onSelectionAnchorChange(null);
            } else {
              // Select all currently loaded assets for the active filter
              const ids = sortedAssets.map(a => a._id);
              onSelectedAssetsChange(ids);
              onSelectionAnchorChange(ids[ids.length - 1] || null);
            }
          }}
          sx={{
            order: 1,
            height: '40px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            backgroundColor: selectedAssetIds.length > 0 ? '#072f53' : 'transparent',
            borderColor: '#072f53',
            color: selectedAssetIds.length > 0 ? '#fff' : '#072f53',
            '&:hover': {
              backgroundColor: selectedAssetIds.length > 0 ? '#0a4a7a' : 'rgba(7,47,83,0.1)',
              borderColor: '#072f53'
            }
          }}
        >
          {selectedAssetIds.length > 0 ? `Avmarkera (${selectedAssetIds.length})` : 'Markera allt'}
        </Button>
        )}

        {/* Unified View Mode + Size Control */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: '#f8f8f8',
          padding: compactToolbar ? '2px' : '4px',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          height: compactToolbar ? '30px' : '40px',
          order: 2
        }}>
          <Tooltip title="Små bilder">
            <IconButton
              size="small"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); suppressNextClicks(); onViewModeChange('grid'); onThumbnailSizeChange?.({} as any, PRESETS.MIN.thumb); }}
              sx={{
                backgroundColor: viewMode === 'grid' && activePreset === 'MIN' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: viewMode === 'grid' && activePreset === 'MIN' ? '#072f53' : '#555',
                mx: 0.25,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mellanstora bilder">
            <IconButton
              size="small"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); suppressNextClicks(); onViewModeChange('grid'); onThumbnailSizeChange?.({} as any, PRESETS.MED.thumb); }}
              sx={{
                backgroundColor: viewMode === 'grid' && activePreset === 'MED' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: viewMode === 'grid' && activePreset === 'MED' ? '#072f53' : '#555',
                mx: 0.25,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Stora bilder">
            <IconButton
              size="small"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); suppressNextClicks(); onViewModeChange('grid'); onThumbnailSizeChange?.({} as any, PRESETS.MAX.thumb); }}
              sx={{
                backgroundColor: viewMode === 'grid' && activePreset === 'MAX' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: viewMode === 'grid' && activePreset === 'MAX' ? '#072f53' : '#555',
                mx: 0.25,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <ViewModuleIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista">
            <IconButton
              size="small"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); suppressNextClicks(); onViewModeChange('list'); }}
              sx={{
                backgroundColor: viewMode === 'list' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: viewMode === 'list' ? '#072f53' : '#555',
                mx: 0.25,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        {/* Butiksbilder: Butik (level-3) dropdown - shown when a chain under Butiksbilder is selected */}
        {isUnderStoreImages && storeTypeOptions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, order: 2 }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="store-type-filter-label">Butik</InputLabel>
              <Select
                labelId="store-type-filter-label"
                label="Butik"
                value={storeTypeCategoryId || ''}
                onOpen={() => { suppressNextClicks(); }}
                onClose={() => { suppressNextClicks(); }}
                onChange={(e) => { suppressNextClicks(); setStoreTypeCategoryId((e.target.value as string) || ''); }}
              >
                <MenuItem value=""><em>Alla butiker</em></MenuItem>
                {storeTypeOptions.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {/* Kedjor dropdown - only when under Produkter and if chain fields exist */}
        {isUnderProducts && chainFields.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, order: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="chain-filter-label">Kedjor</InputLabel>
              <Select
                labelId="chain-filter-label"
                label="Kedjor"
                value={chainFilterKey || ''}
                onOpen={()=>{ suppressNextClicks(); }}
                onClose={()=>{ suppressNextClicks(); }}
                onChange={(e)=> { suppressNextClicks(); onChainFilterChange && onChainFilterChange((e.target.value as string) || null); }}
              >
                <MenuItem value=""><em>Alla kedjor</em></MenuItem>
                {chainFields.map(cf => (
                  <MenuItem key={cf.id} value={cf.id}>{cf.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {isUnderWine && wineDropdownFields.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, order: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="wine-filter-label">Filter</InputLabel>
              <Select
                labelId="wine-filter-label"
                label="Filter"
                value={wineFilterKey || ''}
                onOpen={() => { suppressNextClicks(); }}
                onClose={() => { suppressNextClicks(); }}
                onChange={(e) => { suppressNextClicks(); onWineFilterChange && onWineFilterChange((e.target.value as string) || null); }}
              >
                <MenuItem value=""><em>Alla filter</em></MenuItem>
                {wineDropdownFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {/* Popular AI tags (Mat category specific) */}
        {popularAiTagsLoading && popularAiTags.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', order: 3 }}>
            <CircularProgress size={20} />
          </div>
        )}
        {popularAiTags.length > 0 && (
          <div style={{ 
            display: 'flex', 
            flexWrap: isTabletToolbar ? 'nowrap' : 'wrap', 
            gap: 8, 
            background: '#f8f8f8', 
            padding: '4px 8px', 
            borderRadius: '4px', 
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            order: 3,
            maxWidth: '100%'
          }}>
            {popularAiTags.map(({ tag, count }) => {
              const isSelected = normalizedSelectedAiTags.has(tag.toLowerCase());
              return (
                <Tooltip key={`popular-ai-tag-${tag}`} title={`${count} assets med taggen`}>
                  <Chip
                    label={tag}
                    onClick={() => onAiTagToggle(tag)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      height: isTabletToolbar ? '28px' : '24px',
                      fontSize: isTabletToolbar ? '12px' : '11px',
                      fontWeight: isSelected ? 'bold' : 'normal',
                      backgroundColor: isSelected ? '#072f53' : 'transparent',
                      color: isSelected ? '#fff' : '#072f53',
                      borderColor: '#072f53',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isSelected ? '#072f53' : 'rgba(7,47,83,0.1)'
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
        )}

        {/* Badge Filter */}
        <div style={{ minWidth: compactToolbar ? 'auto' : isTabletToolbar ? '220px' : '200px', maxWidth: '100%', overflowX: 'auto', order: 4 }}>
          <BadgeFilter
            assets={assets}
            badges={availableBadges}
            selectedBadgeIds={selectedBadgeIds}
            onBadgeToggle={onBadgeToggle}
            onClearBadges={onClearBadges}
            loading={loading}
            filterMode={badgeFilterMode}
            onFilterModeToggle={onBadgeFilterModeToggle}
            activeCategoryIds={activeCategoryIds}
            allCategories={allCategories}
            compact={compactToolbar}
          />
        </div>

        
        {/* Sorteringsmeny */}
        <Menu
          anchorEl={sortMenuAnchorEl}
          open={Boolean(sortMenuAnchorEl)}
          onClose={handleSortMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={() => handleSortChange('newest')}>
            <ListItemIcon>
              <CalendarTodayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Nyast först</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSortChange('oldest')}>
            <ListItemIcon>
              <CalendarTodayIcon fontSize="small" style={{ transform: 'rotate(180deg)' }} />
            </ListItemIcon>
            <ListItemText>Äldst först</ListItemText>
          </MenuItem>
          {showWordPressPublishedSort && (
            <MenuItem onClick={() => handleSortChange('wp_newest')}>
              <ListItemIcon>
                <CalendarTodayIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>WP nyast</ListItemText>
            </MenuItem>
          )}
          {showWordPressPublishedSort && (
            <MenuItem onClick={() => handleSortChange('wp_oldest')}>
              <ListItemIcon>
                <CalendarTodayIcon fontSize="small" style={{ transform: 'rotate(180deg)' }} />
              </ListItemIcon>
              <ListItemText>WP äldst</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleSortChange('name_asc')}>
            <ListItemIcon>
              <SortByAlphaIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Namn (A-Ö)</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSortChange('name_desc')}>
            <ListItemIcon>
              <SortByAlphaIcon fontSize="small" style={{ transform: 'scaleX(-1)' }} />
            </ListItemIcon>
            <ListItemText>Namn (Ö-A)</ListItemText>
          </MenuItem>
          {(isUnderProducts || isUnderWine) && (
            <MenuItem onClick={() => handleSortChange('artikelnummer_asc')}>
              <ListItemIcon>
                <SortIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Artikelnummer (0-9)</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </div>

      {/* Conditional rendering based on viewMode */}
      {viewMode === 'list' ? (
        renderListView()
      ) : (
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${preset.thumb + 30}px, ${preset.thumb + 30}px))`,
          gap: '16px',
          padding: '8px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {visibleAssets.map((asset, index) => (
            <GridAssetCard
              key={asset._id}
              asset={asset}
              index={index}
              isSelected={selectedIdSet.has(asset._id)}
              isDuplicate={duplicateAssetIds.has(asset._id)}
              isStoreAsset={storeAssetIds.has(asset._id)}
              isKontorAsset={isKontor(asset)}
              stockLevel={stockLevels[asset._id]}
              stockLevelsLoading={stockLevelsLoading}
              isUnderProducts={isUnderProducts}
              isUnderWine={isUnderWine}
              isProductsAsset={isProductsAsset(asset)}
              isWineAsset={isWineAsset(asset)}
              isRecipeAsset={isRecipeAsset(asset)}
              preset={preset}
              imageInnerPadding={imageInnerPadding}
              textPadTop={textPadTop}
              textPadX={textPadX}
              textPadBottom={textPadBottom}
              cardTitle={getCardTitle(asset)}
              productMetaLine={getProductMetaLine(asset)}
              wineMetaLine={getWineMetaLine(asset)}
              categoryById={categoryById}
              onAssetClick={handleAssetClick}
              onDragStart={handleDragStart}
              onMenuOpen={handleMenuOpen}
              onDuplicateClick={handleDuplicateIndicatorClick}
              onAddToCart={addToCart}
              getStockColor={getStockColor}
              isBadgeVisibleInCurrentView={isBadgeVisibleInCurrentView}
            />
          ))}
        </div>
      )}
      {/* Lazy load sentinel + counter */}
      {hasMoreAssets && <div ref={sentinelCallbackRef} style={{ height: 1 }} />}
      {!loading && sortedAssets.length > 0 && (
        <Typography variant="body2" sx={{ textAlign: 'center', py: 1, color: '#999', fontSize: '0.8rem' }}>
          Visar {visibleAssets.length} av {totalAssetCount ?? sortedAssets.length} assets
          {hasMoreAssets && ' — scrolla för att visa fler'}
        </Typography>
      )}
      {/* Drag selection rectangle - always mounted, visibility controlled by isDraggingSelect */}
      <div 
        ref={dragBoxRef}
        style={{ 
          position: 'fixed', 
          left: 0, 
          top: 0, 
          width: 0, 
          height: 0, 
          border: '1px dashed #072f53', 
          background: 'rgba(7,47,83,0.1)', 
          pointerEvents: 'none', 
          zIndex: 2000,
          display: isDraggingSelect ? 'block' : 'none'
        }} 
      />
      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      ) : visibleAssets.length === 0 && (showNoAssetsMessage || ((dabasStatusFilter !== 'all' || kontorFilter !== 'all') && (isUnderProducts || isUnderWine))) && (
        <Box sx={{ py: 4, textAlign: 'center', transition: 'opacity 0.5s ease-in', opacity: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {(dabasStatusFilter !== 'all' || kontorFilter !== 'all') && isUnderProducts
              ? 'Inga produkter matchar vald status'
              : (dabasStatusFilter !== 'all' || kontorFilter !== 'all') && isUnderWine
                ? 'Inga viner matchar vald status'
                : 'Inga assets hittades'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(dabasStatusFilter !== 'all' || kontorFilter !== 'all') && isUnderProducts
              ? 'Ändra statusfiltret för att visa fler produkter.'
              : (dabasStatusFilter !== 'all' || kontorFilter !== 'all') && isUnderWine
                ? 'Ändra statusfiltret för att visa fler viner.'
                : 'Försök att ladda om sidan eller ändra dina sökfilter'}
          </Typography>
        </Box>
      )}


      
      {/* Context Menu - optimized for instant open/close */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        disablePortal
        transitionDuration={0}
        MenuListProps={{
          dense: true,
          sx: { py: 1 },
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        }}
        PaperProps={{
          elevation: 3,
          sx: { 
            width: 220,
            maxWidth: '100%',
            borderRadius: '8px',
            mt: 0.5,
            bgcolor: '#f8f8f8',
            border: '1px solid rgba(0,0,0,0.08)'
          },
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        }}
      >
        {selectedAssetForMenu ? [
          (
            canShare ? (
            <MenuItem key="share" onClick={handleShare} sx={{ py: 1, px: 2, minHeight: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <ShareIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              </ListItemIcon>
              <ListItemText primary="Kopiera länk" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
            </MenuItem>
            ) : null
          ),
          (
            canAddToCollection ? (
            <MenuItem key="add-to-collection" onClick={(event) => handleAddToCollectionClick(event, selectedAssetForMenu)} sx={{ py: 1, px: 2, minHeight: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CollectionsIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              </ListItemIcon>
              <ListItemText primary="Lägg till i samling" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
            </MenuItem>
            ) : null
          ),
          (
            canManageBadges ? (
            <MenuItem 
              key="badges" 
              onClick={(event) => { 
                event.stopPropagation(); 
                const multiSelectionActive = selectedAssetIds.length >= 2 && selectedIdSet.has(selectedAssetForMenu._id);
                const assetIdsForDialog = multiSelectionActive ? selectedAssetIds : [selectedAssetForMenu._id];
                const assetForDialog = multiSelectionActive ? null : selectedAssetForMenu;
                onManageAssetBadges?.(assetForDialog, assetIdsForDialog);
                handleMenuClose(); 
              }} 
              sx={{ py: 1, px: 2, minHeight: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <PaletteIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              </ListItemIcon>
              <ListItemText primary="Hantera märken" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
            </MenuItem>
            ) : null
          ),
          (
            canArchive ? (
            <MenuItem key="archive" onClick={handleArchive} sx={{ py: 1, px: 2, minHeight: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <ArchiveIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              </ListItemIcon>
              <ListItemText primary="Arkivera" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
            </MenuItem>
            ) : null
          ),
          (
            activeCollectionId && canAddToCollection ? (
            <MenuItem key="remove-from-collection" onClick={(e) => {
              e.stopPropagation();
              const ids = getAssetIdsForCurrentSelection();
              onRemoveFromCollection?.(ids);
              handleMenuClose();
            }} sx={{ py: 1, px: 2, minHeight: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <PlaylistRemoveIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
              </ListItemIcon>
              <ListItemText primary="Ta bort från samling" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
            </MenuItem>
            ) : null
          ),
          (
            canUpdate ? (
              <MenuItem key="kontor-toggle" onClick={(e) => { e.stopPropagation(); handleToggleKontor(); }} sx={{ py: 1, px: 2, minHeight: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <LensIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={isKontor(selectedAssetForMenu) ? 'Avmarkera "Kontor"' : 'Markera som "Kontor"'}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }}
                />
              </MenuItem>
            ) : null
          ),
          (<Divider key="div-1" sx={{ my: 0.75 }} />),
          (
            <MenuItem key="label" disabled sx={{ py: 0.5, px: 2, minHeight: 0 }}>
              <ListItemText
                primary="Välj nedladdningsformat"
                primaryTypographyProps={{ fontSize: '0.65rem', fontWeight: 400, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.03em' }}
              />
            </MenuItem>
          ),
          // Built-in download options
          ...getAvailableBuiltInDownloadTemplates(selectedAssetForMenu).map((template) => (
            <MenuItem key={`tpl-${template.id}`} onClick={(e) => { e.stopPropagation(); handleDownloadWithTemplate(template.id); }} sx={{ py: 1, px: 2, minHeight: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {React.cloneElement(template.icon, { sx: { fontSize: 17, color: 'rgba(0,0,0,0.55)' } })}
              </ListItemIcon>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                <ListItemText
                  primary={template.name}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.3, color: 'rgba(0,0,0,0.85)' }}
                  sx={{ my: 0, minWidth: 0 }}
                />
                <Tooltip title={template.tooltip} placement="left" arrow>
                  <IconButton
                    size="small"
                    aria-label={`Information om ${template.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{ p: 0.25, color: 'rgba(0,0,0,0.45)' }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </MenuItem>
          )),
          ...(loadingDownloadTemplates ? [
            (
              <MenuItem key="download-templates-loading" disabled sx={{ py: 1, px: 2, minHeight: 0 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <ListItemText primary="Laddar egna templates..." primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.5)' }} />
              </MenuItem>
            )
          ] : []),
          ...(downloadTemplatesError ? [
            (
              <MenuItem key="download-templates-error" disabled sx={{ py: 1, px: 2, minHeight: 0 }}>
                <ListItemText primary={downloadTemplatesError} primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.5)' }} />
              </MenuItem>
            )
          ] : []),
          // Only show "Egna templates" section if there are any
          ...(activeDownloadTemplates.length > 0 ? [
            (<Divider key="div-templates" sx={{ my: 0.75 }} />),
            (
              <MenuItem key="label2" disabled sx={{ py: 0.5, px: 2, minHeight: 0 }}>
                <ListItemText
                  primary="Egna templates"
                  primaryTypographyProps={{ fontSize: '0.65rem', fontWeight: 400, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.03em' }}
                />
              </MenuItem>
            ),
            // Dynamically fetched active templates
            ...activeDownloadTemplates.map((tpl) => (
              <MenuItem key={`dt-${tpl.id}`} onClick={(e) => { e.stopPropagation(); handleRunCustomTemplate(tpl.id); }} sx={{ py: 1, px: 2, minHeight: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <DownloadIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
                </ListItemIcon>
                <ListItemText primary={tpl.name} primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }} />
              </MenuItem>
            ))
          ] : []),
          // Validoo export - only show in Produkter and only for allowed roles
          ...(((isUnderProducts || isUnderWine) && canSaveForValidoo) ? [
            (<Divider key="div-validoo" sx={{ my: 0.75 }} />),
            (
              <MenuItem
                key="validoo-export"
                onClick={(e) => { e.stopPropagation(); handleSaveForValidoo(); }}
                disabled={isSavingForValidoo || (selectedAssetIds.length >= 2 && selectedIdSet.has(selectedAssetForMenu._id))}
                sx={{ py: 1, px: 2, minHeight: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isSavingForValidoo ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Spara för Validoo"
                  secondary={
                    selectedAssetIds.length >= 2 && selectedIdSet.has(selectedAssetForMenu._id)
                      ? 'En produkt åt gången i denna version'
                      : 'Laddar ned original-JPG och planogram-PNG'
                  }
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }}
                  secondaryTypographyProps={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'rgba(0,0,0,0.5)' }}
                />
              </MenuItem>
            )
          ] : []),
          // PDF templates section - only show when under Produkter or Vin
          ...((isUnderProducts || isUnderWine) ? [
            (<Divider key="div-pdf" sx={{ my: 0.75 }} />),
            (
              <MenuItem key="label-pdf" disabled sx={{ py: 0.5, px: 2, minHeight: 0 }}>
                <ListItemText
                  primary="Skapa produktblad (PDF)"
                  primaryTypographyProps={{ fontSize: '0.65rem', fontWeight: 400, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.03em' }}
                />
              </MenuItem>
            ),
            ...(loadingPdfTemplates ? [
              <MenuItem key="pdf-loading" disabled sx={{ py: 1, px: 2, minHeight: 0 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <ListItemText primary="Laddar PDF-mallar..." primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.5)' }} />
              </MenuItem>
            ] : visiblePdfTemplates.length === 0 ? [
              <MenuItem key="pdf-empty" disabled sx={{ py: 1, px: 2, minHeight: 0 }}>
                <ListItemText primary="Inga PDF-mallar tillgängliga" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.5)' }} />
              </MenuItem>
            ] : visiblePdfTemplates.map((tpl) => (
              <MenuItem 
                key={`pdf-${tpl._id}`} 
                onClick={(e) => { e.stopPropagation(); handlePdfDownload(tpl._id!); }} 
                disabled={downloadingPdf}
                sx={{ py: 1, px: 2, minHeight: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <PictureAsPdfIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.55)' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={tpl.name}
                  secondary={[
                    tpl.type === 'products' ? 'Produkter' : 'Vin',
                    (tpl as any).templateMode === 'multi' ? `Multi${(tpl as any).maxProducts ? `, max ${(tpl as any).maxProducts}` : ''}` : 'En produkt',
                    tpl.description || '',
                  ].filter(Boolean).join(' • ')}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400, color: 'rgba(0,0,0,0.85)' }}
                  secondaryTypographyProps={{ fontSize: '0.65rem', fontWeight: 400, color: 'rgba(0,0,0,0.5)' }}
                />
              </MenuItem>
            )))
          ] : [])
        ] : null}
      </Menu>
      
      {/* Add to Collection Dialog */}
      <Dialog
        open={addToCollectionDialogOpen}
        onClose={handleAddToCollectionDialogClose}
      >
        <DialogTitle>Lägg till i samling</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Välj en befintlig samling eller skapa en ny.
          </DialogContentText>
          
          {(selectedAssetForMenu || pendingCollectionAssetIds.length > 0) && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="subtitle2">
                {pendingCollectionAssetIds.length > 1 ? `Valda tillgångar (${pendingCollectionAssetIds.length})` : 'Vald tillgång:'}
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {pendingCollectionAssetIds.length > 1
                  ? `${pendingCollectionAssetIds.length} markerade tillgångar`
                  : firstPendingAssetName}
              </Typography>
            </Box>
          )}
          
          {collectionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {collectionError}
            </Alert>
          )}
          
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              value={createNewCollection ? 'new' : 'existing'}
              onChange={(e) => setCreateNewCollection(e.target.value === 'new')}
            >
              <FormControlLabel 
                value="existing" 
                control={<Radio />} 
                label="Lägg till i befintlig samling" 
              />
              
              {!createNewCollection && (
                <FormControl fullWidth sx={{ mt: 1, ml: 4 }}>
                  <InputLabel id="collection-select-label">Välj samling</InputLabel>
                  <Select
                    labelId="collection-select-label"
                    value={selectedCollection || ''}
                    onChange={(e: SelectChangeEvent) => setSelectedCollection(e.target.value as string)}
                    disabled={addingToCollection}
                  >
                    {collections.map((collection) => (
                      <MenuItem key={collection._id} value={collection._id}>
                        {collection.name} ({collection.assetCount ?? collection.assets.length} tillgångar)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <FormControlLabel 
                value="new" 
                control={<Radio />} 
                label="Skapa ny samling" 
              />
              
              {createNewCollection && (
                <TextField
                  label="Samlingens namn"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  fullWidth
                  sx={{ mt: 1, ml: 4 }}
                  disabled={addingToCollection}
                />
              )}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddToCollectionDialogClose} disabled={addingToCollection}>
            Avbryt
          </Button>
          <Button 
            onClick={handleAddToCollection} 
            variant="contained" 
            disabled={addingToCollection || (
              !createNewCollection && !selectedCollection) || 
              (createNewCollection && !newCollectionName.trim())
            }
          >
            {addingToCollection ? <CircularProgress size={24} /> : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
      <RelationImpactDialog
        open={relationImpactDialogOpen}
        action="archive"
        asset={relationImpactAssets[0] || null}
        assets={relationImpactAssets}
        assetIds={relationImpactAssetIds}
        wordPressImpacts={wordPressArchiveImpacts}
        onClose={() => {
          setRelationImpactDialogOpen(false);
          setRelationImpactAssets([]);
          setRelationImpactAssetIds([]);
          setWordPressArchiveImpacts([]);
        }}
        onConfirm={handleRelationArchiveConfirm}
        onConfirmWithWordPressUnpublish={handleRelationArchiveConfirmWithWordPressUnpublish}
        onCollectionUpdated={onCollectionUpdated}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <ValidooExportBlockedDialog
        open={!!validooErrorInfo}
        errorInfo={validooErrorInfo}
        onClose={closeValidooErrorDialog}
      />

      <ValidooExportSuccessDialog
        open={!!validooSuccessResult}
        result={validooSuccessResult}
        onClose={closeValidooSuccessDialog}
      />

      {/* Duplicate Management Modal */}
      <Dialog
        open={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setDuplicateClusterData(null);
          setDuplicateModalAssetId(null);
          setDuplicateModalSelectedA(null);
          setDuplicateModalSelectedB(null);
        }}
        maxWidth="md"
        fullWidth
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        onMouseDown={(event: React.MouseEvent) => event.stopPropagation()}
        onMouseUp={(event: React.MouseEvent) => event.stopPropagation()}
        slotProps={{
          backdrop: {
            sx: { 
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            },
            onClick: (event: React.MouseEvent) => event.stopPropagation(),
            onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
            onMouseUp: (event: React.MouseEvent) => event.stopPropagation()
          }
        }}
        PaperProps={{
          sx: { 
            zIndex: 1,
            pointerEvents: 'auto'
          },
          onClick: (event: React.MouseEvent) => event.stopPropagation(),
          onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
          onMouseUp: (event: React.MouseEvent) => event.stopPropagation()
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Dublett hittad</span>
          <IconButton 
            onClick={() => {
              setDuplicateModalOpen(false);
              setDuplicateModalSelectedA(null);
              setDuplicateModalSelectedB(null);
            }} 
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {duplicateClusterLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : duplicateClusterData && duplicateClusterData.assets?.length >= 2 ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Denna bild finns i <strong>{duplicateClusterData.assets?.length || 0} exemplar</strong> i systemet.
                  Välj vilken bild som ska behållas (A) och vilken som ska arkiveras (B), sedan klicka "Slå ihop".
                </Typography>
              </Alert>
              
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Klicka för att välja A (behålls) och B (arkiveras):
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {duplicateClusterData.assets?.map((asset: any) => {
                  const isA = duplicateModalSelectedA === asset._id;
                  const isB = duplicateModalSelectedB === asset._id;
                  return (
                    <Box 
                      key={asset._id} 
                      onClick={() => {
                        if (isA) {
                          setDuplicateModalSelectedA(null);
                        } else if (isB) {
                          setDuplicateModalSelectedB(null);
                        } else if (!duplicateModalSelectedA) {
                          setDuplicateModalSelectedA(asset._id);
                        } else if (!duplicateModalSelectedB) {
                          setDuplicateModalSelectedB(asset._id);
                        } else {
                          setDuplicateModalSelectedB(asset._id);
                        }
                      }}
                      sx={{ 
                        textAlign: 'center',
                        p: 1,
                        border: isA ? '3px solid #4caf50' : isB ? '3px solid #f44336' : '1px solid #e0e0e0',
                        borderRadius: '8px',
                        bgcolor: isA ? 'rgba(76, 175, 80, 0.1)' : isB ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      {(isA || isB) && (
                        <Box sx={{
                          position: 'absolute',
                          top: -8,
                          left: -8,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: isA ? '#4caf50' : '#f44336',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {isA ? 'A' : 'B'}
                        </Box>
                      )}
                      <Box
                        component="img"
                        src={
                          asset.previewPaths?.thumbnail 
                            ? `${API_BASE_URL}${asset.previewPaths.thumbnail}` 
                            : `${API_BASE_URL}/${(asset.filePath || '').replace(/\\/g, '/')}`
                        }
                        alt={asset.fileName}
                        sx={{ 
                          width: 100, 
                          height: 100, 
                          objectFit: 'contain', 
                          bgcolor: '#f5f5f5', 
                          borderRadius: 1,
                          mb: 1
                        }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.fileName || asset.title || 'Namnlös'}
                      </Typography>
                      {asset._id === duplicateModalAssetId && (
                        <Chip label="Ursprunglig" size="small" color="default" sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
              
              {(duplicateModalSelectedA && duplicateModalSelectedB) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>A (grön)</strong> behålls som huvudbild. <strong>B (röd)</strong> läggs som version på A och arkiveras.
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              Kunde inte hitta dublett-information för denna bild.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', gap: 1, flexWrap: 'wrap' }}>
          <Button 
            variant="text"
            onClick={() => {
              setDuplicateModalOpen(false);
              setDuplicateModalSelectedA(null);
              setDuplicateModalSelectedB(null);
            }}
          >
            Stäng
          </Button>
          <Button 
            variant="outlined"
            color="primary"
            onClick={() => {
              setDuplicateModalOpen(false);
              setDuplicateModalSelectedA(null);
              setDuplicateModalSelectedB(null);
              window.dispatchEvent(new CustomEvent('dam:navigate-duplicates'));
              window.location.hash = 'duplicates';
            }}
          >
            Gå till dublettkontroll
          </Button>
          <Button 
            variant="contained" 
            color="success"
            disabled={!duplicateModalSelectedA || !duplicateModalSelectedB}
            onClick={() => {
              // Open merge dialog with full settings
              setDuplicateMergeDialogOpen(true);
            }}
          >
            Slå ihop med inställningar...
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Merge Dialog with metadata options */}
      {duplicateMergeDialogOpen && duplicateModalSelectedA && duplicateModalSelectedB && (
        <DuplicateMergeDialog
          open={duplicateMergeDialogOpen}
          assetA={duplicateClusterData?.assets?.find((a: any) => a._id === duplicateModalSelectedA)}
          assetB={duplicateClusterData?.assets?.find((a: any) => a._id === duplicateModalSelectedB)}
          onClose={() => {
            setDuplicateMergeDialogOpen(false);
          }}
          onMerged={() => {
            // Remove merged asset from duplicateAssetIds
            setDuplicateAssetIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(duplicateModalSelectedB!);
              if (duplicateClusterData?.assets?.length === 2) {
                newSet.delete(duplicateModalSelectedA!);
              }
              return newSet;
            });
            
            setSnackbarMessage('Bilderna har slagits ihop! B har arkiverats.');
            setSnackbarOpen(true);
            setDuplicateMergeDialogOpen(false);
            setDuplicateModalOpen(false);
            setDuplicateModalSelectedA(null);
            setDuplicateModalSelectedB(null);
            setDuplicateClusterData(null);
          }}
        />
      )}
    </div>
  );
};

export default AssetGrid; 